/**
 * Smart Workout Generator - Unified ML Integration Layer
 * 
 * This is THE integration point that ties all ML/adaptive systems together.
 * Instead of generating static workouts from templates, this:
 * 
 * 1. CHECKS if training is advisable (recovery, ACWR, fatigue)
 * 2. GETS ML predictions for all exercises
 * 3. APPLIES user profile adjustments
 * 4. ENFORCES deload when needed
 * 5. RETURNS an already-optimized workout
 * 
 * The user just trains - they don't decide weights, the system does.
 */

import type { WorkoutProgram, WorkoutDay } from './types/program';
import type { GeneratedExercise, WorkoutTemplate, AdjustmentSource, AppliedAdjustment } from './workoutGenerator';
import { generateWorkout, getWorkoutTemplate } from './workoutGenerator';
import { computeSetVolume } from './volumeTracking';
import { classifyDayType } from './workoutEnrichment';
import { calculateRecoveryDebt, type RecoveryDebtScore } from './recoveryDebt';
import { isMLModelReady } from './mlLoadRecommendation';
import { getHybridRecommendation, type AccessoryRecommendation } from './accessoryAdaptive';
import { getStatisticalRecommendation, type StatisticalRecommendation } from './statisticalRecommendation';
import { detectTrainingLevel, getTrainingLevelThresholds, type TrainingLevel } from './trainingLevelDetection';
import { getTodayRecoveryMetrics, getCurrentReadiness } from './recoveryCalculator';
import { getSessionHistory, type SessionLog, type PTSessionLog } from './sessionStorage';
import { getCurrentUserId } from './anonymousAuth';
import { roundToNearestPlate } from './plateMath';
import { isLoadPlusBodyweightLift } from './oneRM';
import { isAssistedExercise, getTrackingType } from './exerciseDatabase';
import {
  parseCarryDistance,
  getCarryProgression,
  applyCarryDistance,
  type CarryHistoryPoint,
} from './carryProgression';
import {
  parseHoldDuration,
  getTimedHoldProgression,
  applyHoldDuration,
  type TimedHoldHistoryPoint,
} from './timedHoldProgression';

/**
 * CARRIES ARE NOT REP WORK.
 *
 * A carry logs DISTANCE in the `reps` field — a 4×40 farmer's walk is 40
 * METRES per round, not 40 repetitions. Every downstream consumer that reasons
 * about "reps" is therefore reading metres on these lifts, which produced two
 * defects visible in production data:
 *
 *  1. The plateau engine compares maxWeight and maxReps across sessions. A
 *     lifter holding 75 lb and walking a steady 40 m is flat on both axes by
 *     construction, so it read as a stall and cut LOAD — the one term a carry
 *     deliberately holds fixed while distance does the progressing.
 *  2. User-facing copy rendered "you recently completed that for 40+ reps"
 *     when it meant 40+ metres.
 *
 * Detection prefers the curated DB trackingType, but does NOT trust it alone:
 * findExercise() fuzzy-matches short names onto longer unrelated variants, so a
 * bare trackingType check could mislabel an ordinary lift as a carry and
 * silently exempt it from plateau detection. The DB answer therefore only
 * counts when the name also reads like a carry; the name/format patterns stand
 * on their own for freestyle and custom entries that have no DB row.
 */
export function isCarryPrescription(name: string, format?: string): boolean {
  const lower = (name || '').toLowerCase();
  const looksLikeCarry =
    /\bcarry\b|farmer'?s?\s+(walk|carry)|suitcase|yoke|sled\s+(push|drag|pull)|prowler/i.test(lower);
  if (format === 'carry') return true;
  if (looksLikeCarry) return true;
  // DB says carry AND the name is at least carry-adjacent (loaded conditioning)
  return getTrackingType(name) === 'carry' && /walk|drag|push|pull|hold/i.test(lower);
}
import { isPrimaryLift, hasExplicitPercent, deriveWorkingLoadFrom1RM } from './e1rmAnchor';
import { applyProgression } from './autoProgress';
import { getWorkoutSubstitutions, getExerciseSubstitute, type SubstitutionResult } from './exerciseSubstitution';
import { db } from './db';
import { detectPlateau, type SimplePlateauResult } from './plateauDetection';
// movementPatternFatigue is no longer consulted for load — see the LAYER 2 note
// in the per-exercise chain (it was docking 9% off the first working set because
// the enricher's own WARMUP movements matched the pattern lists).
import { getRecoveryHistory } from './recoveryCalculator';
import { detectPeriodizationPhase, type PeriodizationPhase } from './periodization';
import { getCircuitRecommendation, type CircuitRecommendation } from './circuitMLRecommendation';
import { analyzeCardioProgression, analyzeRuckProgression } from './adaptiveCardioPace';
import { cardioTypeParam } from './cardioModality';
import { LAUNCH_FLAGS } from './launchFlags';

// ============================================================================
// TYPES
// ============================================================================

export interface SmartWorkoutResult {
  status: 'ready' | 'deload' | 'rest' | 'recommended-rest' | 'program-complete' | 'reentry';
  workout: GeneratedExercise[] | null;
  
  // Why this decision was made
  reasoning: string[];
  
  // What intelligence was used
  intelligence: {
    recoveryScore: number;
    acwr: number;
    acwrStatus: 'optimal' | 'elevated' | 'high' | 'critical';
    trainingLevel: TrainingLevel;
    mlReady: boolean;
    deloadTriggered: boolean;
    volumeAdjustment: number;  // 1.0 = normal, 0.5 = deload
    intensityAdjustment: number;
    // Periodization context
    periodizationPhase?: {
      name: string;
      type: string;
      focus: string;
    };
    // Reentry ramp context — populated when the reentry gate fires (accepted
    // plan OR auto-detected returning state). Drives the pre-session banner.
    reentry?: {
      triggered: true;
      daysOff: number;
      strengthFactor: number;   // 0-1 multiplier vs pre-break load
      aerobicFactor: number;    // 0-1 multiplier for cardio/circuits
      strengthCapPct: number;   // rounded percent for display
      aerobicCapPct: number;
    };
  };
  
  // Adjustments made
  adjustments: ExerciseAdjustment[];

  // Exercise substitutions for injury prevention
  substitutions: SubstitutionResult[];

  // Synthesis-driven intelligence context
  synthesisContext?: {
    activeSyntheses: Array<{ id: string; title: string; action: string; severity: string }>;
    volumeMultiplier: number;
    intensityMultiplier: number;
    exerciseFlags: Array<{ exerciseName: string; flag: string; detail: string }>;
  };

  // User-facing message
  message: string;
}

// ============================================================================
// 1RM LOOKUP — case + space + punctuation insensitive
// ============================================================================
// Real user data shows duplicate keys with inconsistent casing in the oneRMs
// map (e.g. both `backSquat: 329` and `backsquat: 325`, or `benchPress: 330`
// and `benchpress: 261`). A naive `oneRMs[exercise.name]` lookup misses both
// when the exercise name is "Back Squat" with a space + capitalized.
//
// This helper normalizes the lookup key and returns the MAX matching value
// — duplicates with different casing usually represent the same lift at
// different times; the highest is the conservative-correct baseline.
// ============================================================================
function normalizeExerciseKey(name: string): string {
  return name.toLowerCase().replace(/[\s\-_()]+/g, '');
}

export function lookup1RM(oneRMs: Record<string, number>, exerciseName: string): number {
  if (!exerciseName) return 0;
  // Fast path — exact match (the common case for existing code).
  if (oneRMs[exerciseName] && oneRMs[exerciseName] > 0) return oneRMs[exerciseName];

  const target = normalizeExerciseKey(exerciseName);
  let best = 0;
  for (const key in oneRMs) {
    const v = oneRMs[key];
    if (!v || v <= 0) continue;
    if (normalizeExerciseKey(key) === target) {
      if (v > best) best = v;
    }
  }
  return best;
}

// ============================================================================
// INTELLIGENT FIRST-TIME WEIGHT SUGGESTIONS
// ============================================================================
// For exercises with no prior history AND no 1RM, suggest a conservative
// starting weight based on:
// 1. Related lifts the user HAS done (e.g., bench press → tricep pushdown)
// 2. Exercise category (compound vs isolation vs conditioning)
// 3. Training level (beginner/intermediate/advanced)
// 4. Common gym standards for the movement pattern
// ============================================================================

// Maps exercise names to their typical starting weight as fraction of body weight
// or absolute values for common exercises. Values are for INTERMEDIATE level.
const STARTING_WEIGHT_RULES: Record<string, {
  // If we have a related 1RM, use this fraction of it
  relatedRM?: string;
  rmFraction?: number;
  // Absolute fallback weights by training level (lbs)
  beginner: number;
  intermediate: number;
  advanced: number;
}> = {
  // === LEGS ===
  'Leg Extension': { relatedRM: 'squat', rmFraction: 0.25, beginner: 40, intermediate: 70, advanced: 100 },
  'Seated Ham Curl': { relatedRM: 'deadlift', rmFraction: 0.2, beginner: 30, intermediate: 50, advanced: 80 },
  'Leg Curl': { relatedRM: 'deadlift', rmFraction: 0.2, beginner: 30, intermediate: 50, advanced: 80 },
  'Hip Thrust': { relatedRM: 'squat', rmFraction: 0.6, beginner: 65, intermediate: 135, advanced: 225 },
  'Hip Thrust Ramp': { relatedRM: 'squat', rmFraction: 0.5, beginner: 45, intermediate: 95, advanced: 185 },
  'Goblet Squat': { beginner: 25, intermediate: 45, advanced: 70 },
  'Bulgarian Split Squat': { relatedRM: 'squat', rmFraction: 0.2, beginner: 20, intermediate: 40, advanced: 70 },
  'Walking Lunge': { relatedRM: 'squat', rmFraction: 0.15, beginner: 15, intermediate: 30, advanced: 50 },
  'Step-Ups': { relatedRM: 'squat', rmFraction: 0.15, beginner: 15, intermediate: 35, advanced: 55 },
  'Glute Bridge Iso': { beginner: 45, intermediate: 95, advanced: 155 },
  'Standing Calf Raise': { beginner: 45, intermediate: 90, advanced: 135 },
  'Seated Calf Raise': { beginner: 25, intermediate: 45, advanced: 90 },
  'Soleus Raise': { beginner: 25, intermediate: 45, advanced: 90 },
  'Leg Press': { relatedRM: 'squat', rmFraction: 1.2, beginner: 90, intermediate: 180, advanced: 360 },
  'Hip Abduction': { beginner: 30, intermediate: 50, advanced: 80 },
  'Hip Adduction': { beginner: 30, intermediate: 50, advanced: 80 },

  // === PUSH ===
  'Dumbbell Bench Press': { relatedRM: 'bench', rmFraction: 0.3, beginner: 30, intermediate: 50, advanced: 80 },
  'Incline Dumbbell Press': { relatedRM: 'bench', rmFraction: 0.25, beginner: 25, intermediate: 45, advanced: 70 },
  'Dumbbell Shoulder Press': { relatedRM: 'ohp', rmFraction: 0.3, beginner: 20, intermediate: 40, advanced: 60 },
  'Arnold Press': { relatedRM: 'ohp', rmFraction: 0.25, beginner: 20, intermediate: 35, advanced: 55 },
  'Machine Chest Press': { relatedRM: 'bench', rmFraction: 0.5, beginner: 40, intermediate: 80, advanced: 140 },
  'Dumbbell Fly': { beginner: 15, intermediate: 25, advanced: 40 },
  'Lateral Raise': { beginner: 10, intermediate: 15, advanced: 25 },
  'Cable Lateral Raise': { beginner: 10, intermediate: 15, advanced: 25 },
  'Front Raise': { beginner: 10, intermediate: 15, advanced: 25 },

  // === PULL ===
  'Barbell Row': { relatedRM: 'deadlift', rmFraction: 0.35, beginner: 65, intermediate: 115, advanced: 185 },
  'Dumbbell Row': { relatedRM: 'deadlift', rmFraction: 0.2, beginner: 25, intermediate: 50, advanced: 80 },
  'Lat Pulldown': { beginner: 60, intermediate: 100, advanced: 150 },
  'Cable Row': { beginner: 50, intermediate: 90, advanced: 140 },
  'Chest Supported Row': { beginner: 25, intermediate: 45, advanced: 70 },
  'T-Bar Row': { relatedRM: 'deadlift', rmFraction: 0.3, beginner: 45, intermediate: 90, advanced: 145 },
  'Face Pull': { beginner: 15, intermediate: 30, advanced: 50 },
  'Rear Delt Fly': { beginner: 10, intermediate: 15, advanced: 25 },
  'Straight-Arm Pulldown': { beginner: 20, intermediate: 40, advanced: 60 },
  'Reverse Pec Deck': { beginner: 30, intermediate: 50, advanced: 80 },

  // === ARMS ===
  'Barbell Curl': { beginner: 30, intermediate: 55, advanced: 85 },
  'Dumbbell Curl': { beginner: 15, intermediate: 25, advanced: 40 },
  'Hammer Curl': { beginner: 15, intermediate: 25, advanced: 40 },
  'EZ Bar Curl': { beginner: 30, intermediate: 50, advanced: 75 },
  'Preacher Curl': { beginner: 25, intermediate: 40, advanced: 65 },
  'Tricep Pushdown': { beginner: 25, intermediate: 45, advanced: 70 },
  'Rope Tricep Pushdown': { beginner: 20, intermediate: 40, advanced: 65 },
  'Skull Crusher': { relatedRM: 'bench', rmFraction: 0.25, beginner: 30, intermediate: 55, advanced: 85 },

  // === CORE ===
  'Cable Crunch': { beginner: 30, intermediate: 60, advanced: 100 },

  // === CARRIES & CONDITIONING ===
  'Sled Push': { relatedRM: 'squat', rmFraction: 0.5, beginner: 45, intermediate: 90, advanced: 180 },
  'Backward Drag': { relatedRM: 'squat', rmFraction: 0.35, beginner: 45, intermediate: 70, advanced: 135 },
  'Sled Drag': { relatedRM: 'squat', rmFraction: 0.35, beginner: 45, intermediate: 70, advanced: 135 },
  "Farmer's Carry": { relatedRM: 'deadlift', rmFraction: 0.3, beginner: 35, intermediate: 60, advanced: 90 },
  'Kettlebell Swing': { beginner: 25, intermediate: 45, advanced: 70 },

  // === BACK EXTENSIONS ===
  'Back Extension': { beginner: 0, intermediate: 25, advanced: 45 },
};

// The `relatedRM` tokens above ('squat', 'bench', 'ohp') are short hand names.
// They must be expanded to canonical lift names before the 1RM lookup, because
// lookup1RM matches on a normalized key — 'squat' normalizes to 'squat', but a
// stored back-squat 1RM normalizes to 'backsquat' and never matches. Without
// this expansion every squat/bench/ohp-derived accessory silently ignored the
// user's real strength and fell back to a flat level-based default.
const RELATED_RM_ALIASES: Record<string, string> = {
  squat: 'back squat',
  bench: 'bench press',
  ohp: 'overhead press',
  deadlift: 'deadlift',
};

// True when this exercise is a primary barbell lift AND the user has a usable
// e1RM for it — the conditions under which we anchor the working weight to the
// e1RM via the RPE/RIR table. The anchor math + primary-lift set live in
// ./e1rmAnchor, shared with the swap/freestyle engine (single source of truth).
function isPrimaryLiftWithE1RM(
  exercise: GeneratedExercise,
  oneRMs: Record<string, number>
): boolean {
  const fmt = (exercise.format || '').toLowerCase();
  if (fmt === 'bodyweight' || fmt === 'cardio' || fmt === 'ruck') return false;
  // Case-insensitive: program authors write both 'bodyweight' and 'Bodyweight'
  if ((exercise.intensity || '').toLowerCase() === 'bodyweight') return false;
  if (!isPrimaryLift(exercise.name)) return false;
  return lookup1RM(oneRMs, exercise.name) > 0;
}

function getIntelligentStartingWeight(
  exercise: GeneratedExercise,
  oneRMs: Record<string, number>,
  trainingLevel: TrainingLevel
): number {
  const name = exercise.name;

  // Never put a load on a movement that isn't loaded with a barbell/weight.
  // Guards the own-1RM lookup below from turning a stored rep-count (e.g. a
  // pull-up "1RM" of 31) into a 31 lb barbell prescription.
  //
  // The curated DB trackingType is the strongest signal (the intensity string
  // is authored prose with inconsistent casing — 'bodyweight' AND 'Bodyweight'
  // both exist in lib/programs/). Load-plus-bodyweight lifts (weighted
  // pull-ups/dips) are excepted from the trackingType check on purpose: the
  // comment below documents that they fall through to the conservative
  // rules/fallback weights.
  if ((exercise.trackingType === 'bodyweight-reps' && !isLoadPlusBodyweightLift(name)) ||
      exercise.format === 'bodyweight' ||
      (exercise.intensity || '').toLowerCase() === 'bodyweight' ||
      exercise.format === 'cardio' || exercise.format === 'ruck') {
    return 0;
  }

  // PRIORITY: a tested/estimated 1RM for THIS exact lift is the strongest signal
  // we have — derive the working weight from it before falling back to any
  // generic level bucket. (Fix: advanced lifters were getting an empty-bar 45 lb
  // suggestion on big lifts like Back Squat because the lift wasn't in
  // STARTING_WEIGHT_RULES and its own 1RM was never consulted here.)
  //
  // EXCEPT bodyweight-plus-load lifts (weighted pull-ups/chins/dips): their
  // stored "1RM" is the ADDED load (or a rep count), and % math on added load
  // ignores bodyweight → systematically OVER-prescribes (audit P0-7; full
  // rationale in oneRM.isLoadPlusBodyweightLift). They fall through to the
  // conservative rules/fallback weights instead.
  const own1RM = isLoadPlusBodyweightLift(name) ? 0 : lookup1RM(oneRMs, name);
  if (own1RM > 0) {
    const derived = deriveWorkingLoadFrom1RM(own1RM, exercise);
    if (derived > 0) {
      console.log(` Derived ${name} from its own 1RM (${own1RM}): ${derived} lbs`);
      return derived;
    }
  }

  const rules = STARTING_WEIGHT_RULES[name];

  if (!rules) {
    // No specific rule — try to infer from exercise format/name
    const lowerName = name.toLowerCase();

    // Skip bodyweight, stretch, and cardio exercises (case-insensitive on the
    // authored intensity string; prefer the DB trackingType when present)
    if ((exercise.trackingType === 'bodyweight-reps' && !isLoadPlusBodyweightLift(name)) ||
        exercise.format === 'bodyweight' ||
        (exercise.intensity || '').toLowerCase() === 'bodyweight') return 0;
    if (lowerName.includes('stretch') || lowerName.includes('warm-up') || lowerName.includes('mobility')) return 0;
    if (lowerName.includes('plank') || lowerName.includes('dead bug') || lowerName.includes('bird dog')) return 0;
    if (exercise.format === 'cardio' || exercise.format === 'ruck') return 0;

    // Generic fallback for unmapped weighted exercises
    // Conservative: 20/35/50 lbs for isolation, 45/95/135 for compound
    const isCompound = lowerName.includes('press') || lowerName.includes('row') ||
      lowerName.includes('squat') || lowerName.includes('deadlift') ||
      lowerName.includes('thrust') || lowerName.includes('lunge');

    if (isCompound) {
      return trainingLevel === 'beginner' ? 45 : trainingLevel === 'intermediate' ? 95 : 135;
    }
    // Isolation/accessory — conservative
    return trainingLevel === 'beginner' ? 15 : trainingLevel === 'intermediate' ? 25 : 40;
  }

  // If we have a related 1RM, derive from it (most accurate).
  // Uses the robust lookup helper so case/space variants in the oneRMs
  // map all resolve correctly.
  if (rules.relatedRM && rules.rmFraction) {
    const relatedName = RELATED_RM_ALIASES[rules.relatedRM] ?? rules.relatedRM;
    const related1RM = lookup1RM(oneRMs, relatedName);
    if (related1RM && related1RM > 0) {
      const derived = Math.round(related1RM * rules.rmFraction);
      const floored = Math.max(derived, 5); // Minimum 5 lbs
      console.log(` Derived ${name} from ${rules.relatedRM} 1RM (${related1RM}): ${floored} lbs`);
      return roundToNearestPlate(floored);
    }
  }

  // Fallback to absolute weight by training level. The table has no 'elite'
  // column — elite maps to the ADVANCED bucket (it used to fall through the
  // ternary to INTERMEDIATE, a level lower than 'advanced' users got; the bug
  // was unreachable until the day-one self-report prior made 'elite' possible
  // on day one).
  const weight = trainingLevel === 'beginner' ? rules.beginner
    : trainingLevel === 'intermediate' ? rules.intermediate
    : rules.advanced;
  return roundToNearestPlate(weight);
}

export interface ExerciseAdjustment {
  exerciseName: string;
  originalLoad: number;
  adjustedLoad: number;
  adjustmentPercent: number;
  reason: string;
  source: 'statistics' | 'ml' | 'profile' | 'recovery' | 'deload' | 'rule' | 'first-time-suggestion';
  // Statistical recommendation details (when source='statistics')
  statistical?: {
    method: 'trend' | 'rpe-adjusted' | 'recent-average' | 'program-weight';
    confidence: number;
    dataPoints: number;
    trend?: {
      direction: 'increasing' | 'stable' | 'decreasing';
      ratePerSession: number;
    };
  };
  // Plateau detection data
  plateau?: SimplePlateauResult;
  // Movement pattern fatigue data
  patternFatigue?: { fatigueMultiplier: number; reason: string };
  // Circuit-specific ML data (when exercise is a circuit/AMRAP/EMOM/etc.)
  circuitData?: {
    paceTargetSeconds: number;
    paceTargetDisplay: string;
    paceDegradation: number;
    consistencyScore: number;
    trend: 'improving' | 'maintaining' | 'declining' | 'insufficient_data';
    sprintTarget?: {
      timeSeconds: number;
      display: string;
      message: string;
    };
  };
}

export interface SmartWorkoutOptions {
  // Override recovery check (for testing)
  skipRecoveryCheck?: boolean;
  
  // Override deload (user wants to push through)
  overrideDeload?: boolean;

  // "Train anyway" on a recommended-rest day. SEPARATE intent from
  // overrideDeload: this falls through the rest gate and routes the day
  // through the existing deload-priority cap (0.70 intensity / 0.50 volume)
  // so the low-recovery signal still has a real effect — a lightened
  // session, not a full one. The cap is SUBMITTED to the decision table;
  // the binding cap wins (e.g. an active reentry cap at higher priority
  // still binds instead).
  overrideRest?: boolean;

  // Manual readiness override (user feels different than score)
  manualReadiness?: number;
  
  // Force ML recommendations even for new users
  forceML?: boolean;
}

// ============================================================================
// MAIN FUNCTION
// ============================================================================

/**
 * Generate a smart, ML-optimized workout
 * 
 * This is THE function that should be called instead of generateWorkout()
 * It integrates all ML systems and returns an already-optimized workout.
 */
/**
 * Scale the distance/duration magnitude inside a cardio `sets_reps` string by
 * `factor` (< 1 trims volume). Returns the rewritten string and whether it
 * actually changed.
 *
 * Deliberately conservative about WHAT it touches:
 *  - Interval formats ("5 × 0:30 / 1:00 rest") are left alone — the set count is
 *    already autoregulated upstream in generateWorkout via volumeMultiplier, so
 *    cutting the per-interval duration too would double-count.
 *  - Distance tokens ("3200m", "2 miles", "5 km") scale the number in place.
 *  - A bare duration ("30:00", "1:05:00") scales the total time, floored at 1
 *    min and rounded to the nearest 30 s so the prescription stays clean.
 */
function scaleCardioMagnitude(setsReps: string, factor: number): { text: string; changed: boolean } {
  if (!setsReps || factor >= 0.999) return { text: setsReps, changed: false };

  // Interval / multi-set format — volume already handled by set-count trim.
  if (/^\s*\d+\s*[x×]/i.test(setsReps)) return { text: setsReps, changed: false };

  // Distance: "<number><unit>" (m, meters, km, mi, mile(s), yd, yard(s)).
  const distMatch = setsReps.match(/(\d+(?:\.\d+)?)\s*(km|mi|miles?|meters?|m|yards?|yd)\b/i);
  if (distMatch) {
    const val = parseFloat(distMatch[1]);
    const unit = distMatch[2];
    const scaled = val * factor;
    const roundMeters = /^(m|meters?|yd|yards?)$/i.test(unit);
    const out = roundMeters
      ? String(Math.max(10, Math.round(scaled / 10) * 10))
      : String(Math.round(scaled * 10) / 10);
    const tail = distMatch[0].slice(distMatch[1].length); // preserves spacing + unit
    return { text: setsReps.replace(distMatch[0], `${out}${tail}`), changed: out !== distMatch[1] };
  }

  // Bare duration: MM:SS or H:MM:SS with no leading "N ×".
  const durMatch = setsReps.match(/^\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*$/);
  if (durMatch) {
    const totalSec = durMatch[3]
      ? parseInt(durMatch[1]) * 3600 + parseInt(durMatch[2]) * 60 + parseInt(durMatch[3])
      : parseInt(durMatch[1]) * 60 + parseInt(durMatch[2]);
    const scaledSec = Math.max(60, Math.round((totalSec * factor) / 30) * 30);
    if (scaledSec === totalSec) return { text: setsReps, changed: false };
    const mm = Math.floor(scaledSec / 60);
    const ss = scaledSec % 60;
    return { text: `${mm}:${ss.toString().padStart(2, '0')}`, changed: true };
  }

  return { text: setsReps, changed: false };
}

/** Parse a distance token ("3200m", "2 miles", "5 km", "400 yd") to meters. */
function parseMetersFromText(s: string | undefined): number | undefined {
  if (!s) return undefined;
  const m = s.match(/(\d+(?:\.\d+)?)\s*(km|mi|miles?|meters?|m|yards?|yd)\b/i);
  if (!m) return undefined;
  const v = parseFloat(m[1]);
  const u = m[2].toLowerCase();
  if (u.startsWith('km')) return Math.round(v * 1000);
  if (u.startsWith('mi')) return Math.round(v * 1609.34);
  if (u.startsWith('yd') || u.startsWith('yard')) return Math.round(v * 0.9144);
  return Math.round(v); // already meters
}

/**
 * Recovery-aware scaling for a cardio/ruck exercise, driven by the sim-validated
 * cardio engine (adaptiveCardioPace). Returns a distance/duration cut + a reason
 * when the engine calls for a deload (low recovery or a programmed deload week),
 * an incline reduction for treadmill work, or a reentry aerobic note. Returns
 * null when nothing should change (insufficient history, or the athlete is
 * cleared to hold/progress). As of 2026-08-11 it DOES auto-increase, bounded
 * at +10% per session and only on an ordinary day — see `allowIncrease`.
 *
 * The reentry aerobic cap stays annotate-only for cardio: pace is RPE-driven, so
 * we nudge the user to hold volume rather than silently rewriting their target.
 */
async function computeCardioRecoveryAdjustment(
  exercise: GeneratedExercise,
  recoveryScore: number,
  periodizationPhase: PeriodizationPhase | null,
  reentryTriggered: boolean,
  reentryAerobicFactor: number,
): Promise<{ setsReps: string; targetDistance?: number; targetIncline?: number; paceTarget?: string; reason: string; adjustmentPercent: number } | null> {
  const lower = exercise.name.toLowerCase();
  const isRuck = lower.includes('ruck') || lower.includes('march');

  /**
   * Upward progression is allowed only on an ordinary day.
   *
   * Never on a programmed deload, never while a reentry ramp is capping
   * aerobic work, and never when recovery is genuinely impaired — those are
   * exactly the days the rest of the engine is trying to take load OFF.
   */
  const allowIncrease =
    !reentryTriggered &&
    recoveryScore >= 60 &&
    periodizationPhase?.type !== 'deload';

  let reductionFactor = 1.0;
  let engineReason = '';
  let engineDistance: number | undefined;
  let engineIncline: number | undefined;
  let paceTarget: string | undefined;

  try {
    if (isRuck) {
      const dec = await analyzeRuckProgression(exercise.name, 5, recoveryScore, periodizationPhase ?? undefined);
      // UPWARD progression. Previously only `progressionPath === 'deload'` was
      // read, so the engine's increase decisions were computed and thrown
      // away: measured across 113 aerobic logs it produced 3 increases and 0
      // reached a prescription. An athlete's ruck load never rose from
      // adaptation, only from whatever the template hardcoded.
      if (dec && dec.progressionPath !== 'deload' && allowIncrease) {
        if (dec.suggestedDistance && dec.currentDistance > 0 && dec.suggestedDistance > dec.currentDistance) {
          reductionFactor = dec.suggestedDistance / dec.currentDistance;
          engineDistance = dec.suggestedDistance;
          engineReason = dec.reason;
        } else if (dec.suggestedWeight && dec.currentWeight > 0 && dec.suggestedWeight > dec.currentWeight) {
          // The +5 lb lever (adaptiveCardioPace.ts) had NO reachable caller:
          // it is only set on non-deload paths, and only the deload branch was
          // being read.
          reductionFactor = dec.suggestedWeight / dec.currentWeight;
          engineReason = dec.reason;
        }
      }
      if (dec && dec.progressionPath === 'deload') {
        if (dec.suggestedDistance && dec.currentDistance > 0 && dec.suggestedDistance < dec.currentDistance) {
          reductionFactor = dec.suggestedDistance / dec.currentDistance;
          engineDistance = dec.suggestedDistance;
        } else if (dec.suggestedWeight && dec.currentWeight > 0 && dec.suggestedWeight < dec.currentWeight) {
          reductionFactor = dec.suggestedWeight / dec.currentWeight;
        }
        engineReason = dec.reason;
      }
    } else {
      // Was a substring ternary defaulting to 'run', so an incline walk, an
      // elliptical and "Easy Cardio" were all analysed as running. Shared
      // classifier — see lib/cardioModality.
      const type = cardioTypeParam(exercise.name);
      const dec = await analyzeCardioProgression(exercise.name, type, 6, recoveryScore, periodizationPhase ?? undefined);
      if (dec) {
        if (allowIncrease && dec.suggestedDistance && dec.currentDistance > 0 &&
            dec.suggestedDistance > dec.currentDistance) {
          reductionFactor = dec.suggestedDistance / dec.currentDistance;
          engineDistance = dec.suggestedDistance;
          engineReason = dec.reason;
        }
        if (dec.suggestedDistance && dec.currentDistance > 0 && dec.suggestedDistance < dec.currentDistance) {
          reductionFactor = dec.suggestedDistance / dec.currentDistance;
          engineDistance = dec.suggestedDistance;
          engineReason = dec.reason;
        }
        if (dec.suggestedIncline !== undefined && dec.currentAvgIncline !== undefined &&
            dec.suggestedIncline < dec.currentAvgIncline) {
          engineIncline = dec.suggestedIncline;
          if (!engineReason) engineReason = dec.reason;
        }
        // VDOT pace guidance — running only (VDOT is a run-specific VO2max
        // proxy). Surfaced whether or not we're cutting volume so the athlete
        // gets a data-anchored pace target instead of a bare "moderate".
        if (type === 'run' && dec.vdot && dec.vdot.vdot > 0) {
          paceTarget = `Pace zones (VDOT ${dec.vdot.vdot}): easy ${dec.vdot.easyPacePerMile}/mi, tempo ${dec.vdot.tempoPacePerMile}/mi, threshold ${dec.vdot.thresholdPacePerMile}/mi.`;
        }
      }
    }
  } catch { /* engine is best-effort — fall through to no change */ }

  /**
   * Bounded BOTH ways. The ceiling was 1.0, which made an increase
   * arithmetically unrepresentable no matter what the engine decided — the
   * factor could only ever cut. 1.10 is the conventional weekly ceiling for
   * aerobic volume progression; the floor stays at 0.5.
   */
  reductionFactor = Math.max(0.5, Math.min(allowIncrease ? 1.10 : 1.0, reductionFactor));

  const reentryNote = (reentryTriggered && !isRuck && reentryAerobicFactor < 1.0)
    ? `Reentry: hold aerobic effort near ${Math.round(reentryAerobicFactor * 100)}% of pre-break — match volume, don't push pace.`
    : '';

  const numericCut = reductionFactor < 0.999 || reductionFactor > 1.001;
  const inclineChanged = engineIncline !== undefined;
  if (!numericCut && !inclineChanged && !reentryNote && !paceTarget) return null;

  const scaled = numericCut
    ? scaleCardioMagnitude(exercise.sets_reps, reductionFactor)
    : { text: exercise.sets_reps, changed: false };

  const reason = [engineReason, reentryNote, paceTarget].filter(Boolean).join(' ')
    || `Low recovery (${recoveryScore}/100) — cardio volume trimmed to ${Math.round(reductionFactor * 100)}%.`;

  return {
    setsReps: scaled.text,
    targetDistance: engineDistance,
    targetIncline: engineIncline,
    paceTarget,
    reason,
    // Signed. This was `-Math.round((1 - reductionFactor) * 100)`, which is
    // fine for a cut but reports an increase as a negative number.
    adjustmentPercent: scaled.changed ? Math.round((reductionFactor - 1) * 100) : 0,
  };
}

export async function generateSmartWorkout(
  program: WorkoutProgram,
  week: number,
  day: number,
  oneRMs: Record<string, number>,
  options: SmartWorkoutOptions = {}
): Promise<SmartWorkoutResult> {
  const userId = getCurrentUserId();
  const reasoning: string[] = [];
  const adjustments: ExerciseAdjustment[] = [];
  
  console.log('� Smart Workout Generator: Starting...');
  console.log(` Program: ${program.metadata.name}, Week ${week}, Day ${day}`);
  
  // ============================================================================
  // STEP 1: GATHER ALL INTELLIGENCE
  // ============================================================================
  
  // Get recovery state. Audit #8: use the single-source-of-truth
  // getCurrentReadiness() which re-blends today's subjective survey
  // with fresh PersonalState (latest wearable data). Pre-fix the
  // generator read `latestRecovery.readinessScore` directly — that's
  // the value stored at save-time, stale if overnight wearable data
  // landed (Garmin sync, HealthKit refresh). Manual readiness override
  // (e.g. tester forcing a value) still wins.
  const readiness = await getCurrentReadiness();
  const latestRecovery = readiness?.metrics ?? await getTodayRecoveryMetrics();
  const recoveryScore = options.manualReadiness ?? readiness?.score ?? 70;
  reasoning.push(`Recovery score: ${recoveryScore}/100${readiness?.source === 'fresh-blend' ? ' (fresh blend)' : ''}`);
  
  // Get ACWR and training load analysis
  const recoveryDebt = await calculateRecoveryDebt();
  const acwr = recoveryDebt.acwr;
  reasoning.push(`ACWR: ${acwr.toFixed(2)} (${recoveryDebt.acwrStatus})`);
  
  // Get session history for training level
  const sessions = await getSessionHistory();
  const ptSessions = sessions.filter(s => s.sessionType === 'pt' || s.sessionType === 'assessment');
  
  // Detect training level. Feed in the user's real strength signal — without
  // oneRMs + bodyweight, detectTrainingLevel can't score relative strength, so
  // a returning lifter with few sessions on the current program collapses to
  // 'beginner' (the <20-session override) and big lifts get bottom-bucket
  // starting weights. A 1.6×BW squatter is not a beginner.
  let bodyWeightForLevel: number | undefined;
  let selfReportedLevel: TrainingLevel | undefined;
  let sexForLevel: 'male' | 'female' | undefined;
  try {
    const prof = userId ? await db.userProfiles.get(userId) : undefined;
    bodyWeightForLevel = (prof as any)?.bodyWeight || (prof as any)?.weight || undefined;
    // What they told us in onboarding. Until today this never reached the load
    // math at all — it was read only for ACWR thresholds — so a day-one user was
    // scored 'beginner' no matter what they said. detectTrainingLevel decays it
    // to zero as real sessions arrive; see the day-one prior there.
    selfReportedLevel = (prof as any)?.experienceLevel || undefined;
    // Sex, so relative-strength banding isn't male-normed (which under-scores
    // women — a female "advanced" lifter was landing in the intermediate band).
    const g = (prof as any)?.gender;
    sexForLevel = (g === 'male' || g === 'female') ? g : undefined;
  } catch { /* non-critical — strength scoring just gets skipped */ }

  /**
   * FALL BACK TO THE CANONICAL PROFILE SOURCES.
   *
   * The block above reads Dexie `userProfiles`, but the fields it wants are
   * written to Firestore `users/{uid}` by onboarding and Settings. When the
   * Dexie mirror lacks them — which it does for accounts created before that
   * mirror existed — every one of these comes back undefined and
   * detectTrainingLevel loses its relative-strength branch entirely.
   *
   * Measured on the demo account (140 sessions, benches 205, squats 260):
   * `users/{uid}` holds bodyWeight 175, experienceLevel 'intermediate',
   * gender 'male', yet the pre-session panel reported
   * "Training level: beginner (85% confidence)" and the engine offered
   * "First-time suggestion: 15 lbs based on beginner level".
   *
   * `resolveBodyWeightLbs` is already the single source of truth for weight
   * (smart scale > users.weight > users.bodyWeight), so use it rather than
   * adding a fourth opinion. It returns a DEFAULT when it knows nothing, so
   * only trust a resolved, non-default answer here — feeding a made-up
   * bodyweight into relative-strength scoring would be worse than skipping it.
   */
  /*
   * BOUNDED. Workout generation must never block on the network.
   *
   * The first version of this fallback awaited a Firestore getDoc inline and
   * timed out six load-path invariant tests at 5s — which is exactly what it
   * would do to a user on a bad connection, except they would be staring at a
   * spinner instead of a red test. Guarded three ways: browser only, raced
   * against a short deadline, and every failure degrades to the previous
   * behaviour (level detection simply skips its relative-strength branch).
   */
  const PROFILE_LOOKUP_MS = 1500;
  const withDeadline = <T,>(p: Promise<T>): Promise<T | undefined> =>
    Promise.race([
      p.catch(() => undefined),
      new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), PROFILE_LOOKUP_MS)),
    ]);

  if (typeof window !== 'undefined' && userId
      && (bodyWeightForLevel === undefined || selfReportedLevel === undefined || sexForLevel === undefined)) {
    try {
      if (bodyWeightForLevel === undefined) {
        const mod = await withDeadline(import('./weightResolver'));
        const resolved = mod ? await withDeadline(mod.resolveBodyWeightLbs(userId)) : undefined;
        if (resolved && resolved.source !== 'default' && resolved.lbs > 0) bodyWeightForLevel = resolved.lbs;
      }
      if (selfReportedLevel === undefined || sexForLevel === undefined) {
        const u: any = await withDeadline((async () => {
          const { doc: fsDoc, getDoc: fsGetDoc } = await import('firebase/firestore');
          const { db: firestoreDb } = await import('./firebase');
          if (!firestoreDb) return undefined;
          const snap = await fsGetDoc(fsDoc(firestoreDb, 'users', userId));
          return snap.exists() ? snap.data() : undefined;
        })());
        if (u) {
          if (selfReportedLevel === undefined && u.experienceLevel) selfReportedLevel = u.experienceLevel;
          if (sexForLevel === undefined && (u.gender === 'male' || u.gender === 'female')) sexForLevel = u.gender;
        }
      }
    } catch { /* non-critical — same degradation as before, just less often */ }
  }

  const trainingLevelResult = detectTrainingLevel({
    operatorScore: estimateOperatorScoreFromSessions(ptSessions),
    totalSessions: ptSessions.length,
    timeInSystemDays: calculateDaysInSystem(ptSessions),
    avgRPE: calculateAvgRPE(ptSessions),
    progressionRate: calculateProgressionRate(ptSessions),
    selfReportedLevel,
    sex: sexForLevel,
    oneRMs: {
      squat: lookup1RM(oneRMs, 'back squat') || undefined,
      benchPress: lookup1RM(oneRMs, 'bench press') || undefined,
      deadlift: lookup1RM(oneRMs, 'deadlift') || undefined,
    },
    bodyweight: bodyWeightForLevel,
  });
  
  const trainingLevel = trainingLevelResult.level;
  const defaultThresholds = getTrainingLevelThresholds(trainingLevel);
  
  //  NEW: Learn personalized thresholds from user's actual training history
  // These override generic defaults based on THEIR performance patterns
  let personalizedThresholds: any = null;
  let recoveryMin = 40; // Default minimum recovery
  let recoveryOptimal = 70; // Default optimal recovery
  let acwrDeloadTrigger = defaultThresholds.acwr.max + 0.2;
  let acwrCriticalTrigger = defaultThresholds.acwr.max + 0.4;
  
  try {
    const recoveryHistory = await getRecoveryHistory();
    
    // Only learn personalized thresholds if we have enough data
    // LAUNCH_FLAGS.tensorflowModels gates the dynamic import, so TensorFlow.js
    // is never fetched. Falls through to the generic thresholds below.
    if (LAUNCH_FLAGS.tensorflowModels && ptSessions.length >= 20 && recoveryHistory.length >= 20) {
      const { learnPersonalizedThresholds: learnThresholds } = await import('./mlUserProfiling');
      personalizedThresholds = await learnThresholds(sessions, recoveryHistory);
      
      // Only use if confidence is reasonable (> 40%)
      if (personalizedThresholds.confidence >= 0.4) {
        recoveryMin = personalizedThresholds.recoveryMin;
        recoveryOptimal = personalizedThresholds.recoveryOptimal;
        acwrDeloadTrigger = personalizedThresholds.acwrDeload;
        acwrCriticalTrigger = personalizedThresholds.acwrCritical;
        
        reasoning.push(` Using PERSONALIZED thresholds (${(personalizedThresholds.confidence * 100).toFixed(0)}% confidence)`);
        reasoning.push(`  Recovery: ${recoveryMin}-${recoveryOptimal} (learned from YOUR data)`);
        reasoning.push(`  ACWR: ${defaultThresholds.acwr.min.toFixed(2)}-${personalizedThresholds.acwrMax.toFixed(2)} (vs generic ${defaultThresholds.acwr.max})`);
        console.log(` Using personalized thresholds learned from ${personalizedThresholds.sessionsAnalyzed} sessions!`);
      } else {
        reasoning.push(`Personalized thresholds learned but low confidence (${(personalizedThresholds.confidence * 100).toFixed(0)}%), using training level defaults`);
      }
    }
  } catch (error) {
    console.log(' Could not learn personalized thresholds:', error);
    reasoning.push('Using standard training level thresholds');
  }
  
  reasoning.push(`Training level: ${trainingLevel} (${(trainingLevelResult.confidence * 100).toFixed(0)}% confidence)`);
  
  // Check if ML is ready. mlReady stays in the intelligence payload (session
  // page + WorkoutAdjustmentsBanner consume it) but is no longer pushed into
  // user-facing reasoning — "neural-net mode" claimed intelligence that wasn't
  // driving the prescription (2026-07 ML plan, Batch A).
  const mlReady = await isMLModelReady();

  // Detect periodization phase for context
  let periodizationPhase: PeriodizationPhase | null = null;
  try {
    const totalWeeks = program.metadata.duration || 12;
    periodizationPhase = detectPeriodizationPhase(week, totalWeeks);
    if (periodizationPhase) {
      reasoning.push(`Phase: ${periodizationPhase.name} (${periodizationPhase.focus})`);
      console.log(` Periodization Phase: ${periodizationPhase.name} | Focus: ${periodizationPhase.focus}`);
    }
  } catch (error) {
    console.log(' Periodization detection skipped:', error);
  }
  
  // ML Capacity Prediction — REMOVED entirely (2026-07 ML plan, Batch A).
  // It stopped influencing the load at audit P0-1 (see the ML CAPACITY BLEND
  // note below), but the fake surface survived: a hardcoded-confidence
  // "Capacity prediction: NN% (adaptive model)" reasoning line and a write-only
  // intelligence.mlCapacity payload. Both deleted; the profiler's dead capacity
  // methods are a separate follow-up excision (learnPersonalizedThresholds in
  // mlUserProfiling is live and untouched).

  // ============================================================================
  // STEP 2: DETERMINE ACWR STATUS WITH PERSONALIZED THRESHOLDS
  // ============================================================================
  
  // Use personalized thresholds if available, otherwise use training level defaults
  const acwrMax = personalizedThresholds?.acwrMax || defaultThresholds.acwr.max;
  
  let acwrStatus: 'optimal' | 'elevated' | 'high' | 'critical';
  if (acwr >= defaultThresholds.acwr.min && acwr <= acwrMax) {
    acwrStatus = 'optimal';
  } else if (acwr > acwrMax && acwr <= acwrDeloadTrigger) {
    acwrStatus = 'elevated';
  } else if (acwr > acwrDeloadTrigger && acwr <= acwrCriticalTrigger) {
    acwrStatus = 'high';
  } else if (acwr > acwrCriticalTrigger) {
    // Only set to 'critical' if ACWR is too HIGH (overtraining risk)
    acwrStatus = 'critical';
  } else {
    // ACWR is below minimum (too low) - still optimal, just undertrained
    acwrStatus = 'optimal';
  }
  
  // ============================================================================
  // STEP 3: CHECK IF TRAINING IS ADVISABLE
  // ============================================================================
  
  // Check 1: Recovery too low (using personalized threshold if available)
  //  RECOMMENDATION, not enforcement - user can override if they understand the risk
  // IMPORTANT: Only check if user ACTUALLY logged recovery today. Default score of 70
  // should NOT trigger a rest day — it just means we don't have data.
  const hasRealRecoveryData = !!latestRecovery;
  if (hasRealRecoveryData && recoveryScore < recoveryMin && !options.skipRecoveryCheck && !options.overrideRest) {
    console.log(` Smart Workout: Recovery too low - REST DAY RECOMMENDED (threshold: ${recoveryMin})`);
    // Record sanctioned rest so Spearhead doesn't penalize this day
    if (userId) {
      import('./spearheadEngine').then(m => m.recordSanctionedRestDay(userId!)).catch(() => {});
    }
    return {
      status: 'recommended-rest',
      workout: null,
      reasoning: [
        ...reasoning,
        `Recovery score ${recoveryScore} is below optimal threshold (${recoveryMin})`,
        'Rest recommended to prevent overtraining and optimize performance',
        'You can override this if you feel capable - the ML will learn from your decision',
      ],
      intelligence: {
        recoveryScore,
        acwr,
        acwrStatus,
        trainingLevel,
        mlReady,
        deloadTriggered: false,
        volumeAdjustment: 0,
        intensityAdjustment: 0,
        periodizationPhase: periodizationPhase ? {
          name: periodizationPhase.name,
          type: periodizationPhase.type,
          focus: periodizationPhase.focus,
        } : undefined,
      },
      adjustments: [],
      substitutions: [],
      message: `Rest day recommended. Recovery score (${recoveryScore}) is below your optimal threshold. If you train anyway, the ML will track your performance and learn from it.`,
    };
  }
  
  // Check 2: ACWR is NO LONGER a rest gate.
  // The acute:chronic workload ratio has no demonstrated predictive value for
  // injury/load management — its apparent signal is a mathematical artifact of
  // coupling (Impellizzeri et al. 2020, Sports Med; Coyne et al. 2021 "Time to
  // Dismiss ACWR", Sports Med — real ACWR predicted injury no better than a
  // RANDOM chronic load). So a high ratio alone must not force rest. ACWR stays
  // a SOFT monitor (intelligence.acwr + reasoning); genuine rest is driven by
  // recovery score (Check 1) and deloads by uncoupled fatigue signals below.
  if (acwrStatus === 'critical' || acwrStatus === 'high') {
    reasoning.push(`ACWR ${acwr.toFixed(2)} elevated — monitor only (not a training gate; recovery + RPE drive decisions)`);
  }

  // ============================================================================
  // STEP 4: DETERMINE ADJUSTMENTS NEEDED
  // ============================================================================
  
  let volumeAdjustment = 1.0;
  let intensityAdjustment = 1.0;
  let deloadTriggered = false;
  let reentryTriggered = false;
  let reentryFactor = 1.0;
  // Decision-table caps. Engines submit IntensityCap records; after all
  // engines have run, decideIntensity() picks the BINDING constraint
  // (highest priority cap per axis) and replaces intensityAdjustment +
  // volumeAdjustment with the result. Eliminates multiplicative cap
  // stacking — e.g. reentry × deload × low-recovery × consec_days
  // would compound to <50% before; now reentry wins (priority 1000)
  // and the others go into suppressed.
  const { decideIntensity, Cap } = await import('./intensityDecisionTable');
  const caps: import('./intensityDecisionTable').IntensityCap[] = [];
  // Short per-exercise reason string. Built once when the reentry branch
  // fires (accepted vs unaccepted picks different copy), then reused for
  // every adjusted lift below so the coach panel reads the same thing.
  // Without this, every lift recomputed `${reentryDays}d off` from the
  // frozen accepted-plan snapshot — which stops matching reality the
  // moment the user takes another short break inside the ramp.
  let reentryReasonShort = '';
  /**
   * Aerobic-specific cap for ruck/cardio/circuit during the reentry ramp.
   * VO2max detrains ~2× faster than 1RM (Mujika & Padilla), so we cap
   * conditioning more aggressively than lifting. 1.0 = no cap; <1 = scaled.
   */
  let reentryAerobicFactor = 1.0;
  let reentryDays = 0;
  let status: 'ready' | 'deload' | 'rest' | 'reentry' = 'ready';

  // ============================================================================
  // REENTRY GATE — when the user has been gone 14+ days, ignore stale
  // progression suggestions and cap working sets per the return-plan model
  // (50-85% of pre-break load, scaled by time off).
  // Runs before deload because they're orthogonal — a returning user can
  // also be flagged for deload, but the reentry cap is the binding floor.
  //
  // BYPASS: if the user's program is reentry-friendly (operator-ramp-up,
  // recruit, first-mile, foundations programs), the program's own
  // progression IS the ramp — stacking a second cap on top would
  // double-cap loads and produce comically light sessions. The gate
  // still records the reentry status for UI surface but skips the cap.
  // ============================================================================
  const { isReentryFriendlyProgram } = await import('./acceptedReentryPlan');
  const programIsReentryFriendly = isReentryFriendlyProgram(program.metadata.id);
  try {
    const { reentryStateFromHistory } = await import('./reentryState');
    const { buildReturnPlan } = await import('./returnToTrainingPlan');
    const { loadAcceptedReentryPlan, intensityFactorFor, aerobicFactorFor, clearAcceptedReentryPlan, rampWeekOf } = await import('./acceptedReentryPlan');

    // Sessions alone are enough to classify status; recovery/nutrition only
    // affect the missedRecovery / missedNutrition flags, which the workout
    // generator doesn't use.
    const reentryState = reentryStateFromHistory(ptSessions, [], []);

    // Accepted plan takes precedence over per-render detection. It locks in
    // the ramp schedule so the cap relaxes predictably from week 1 → full
    // intensity. Without this, a user who started training again would flip
    // back to "on-track" status the moment they logged a session and lose
    // the ramp protection on day 2.
    const { getCurrentUserId } = await import('./anonymousAuth');
    const uid = getCurrentUserId();
    const acceptedPlan = uid ? await loadAcceptedReentryPlan(uid) : null;

    if (acceptedPlan) {
      const factor = intensityFactorFor(acceptedPlan);
      if (factor === null) {
        // Ramp complete — auto-clear and resume normal training.
        if (uid) {
          await clearAcceptedReentryPlan(uid).catch(() => {});
        }
        console.log(' Reentry ramp complete — accepted plan cleared');
      } else if (programIsReentryFriendly) {
        // Program is already a ramp — trust the template, don't double-cap.
        // Still flag status so the UI can surface "reentry mode" copy.
        reentryTriggered = true;
        reentryFactor = 1.0;
        reentryAerobicFactor = 1.0;
        reentryDays = acceptedPlan.daysOffAtAccept;
        status = 'reentry';
        reentryReasonShort = `REENTRY: reentry-friendly program — no cap stacked`;
        reasoning.push(
          `REENTRY (accepted, bypass): program "${program.metadata.name}" is reentry-friendly — trusting program progression, no cap stacked.`,
        );
        console.log(` Reentry cap bypassed — program ${program.metadata.id} is reentry-friendly`);
      } else {
        const week = rampWeekOf(acceptedPlan);
        reentryTriggered = true;
        reentryFactor = factor;
        // Aerobic uses the stored cap from the accepted plan; falls back to
        // strength × 0.85 if the plan predates the field.
        reentryAerobicFactor = aerobicFactorFor(acceptedPlan) ?? Math.max(0.45, factor * 0.85);
        reentryDays = acceptedPlan.daysOffAtAccept;
        intensityAdjustment *= reentryFactor;
        caps.push(Cap.reentry(reentryFactor, 1.0, week, acceptedPlan.rampWeeks));
        status = 'reentry';
        reentryReasonShort = `REENTRY ramp wk ${week}/${acceptedPlan.rampWeeks} — cap at ${Math.round(factor * 100)}%`;
        reasoning.push(
          `REENTRY (accepted): ramp week ${week} of ${acceptedPlan.rampWeeks} → strength cap ${Math.round(factor * 100)}%, aerobic cap ${Math.round(reentryAerobicFactor * 100)}%. ` +
          `${acceptedPlan.daysOffAtAccept} days off at start of plan.`,
        );
        console.log(` Reentry ramp week ${week}/${acceptedPlan.rampWeeks}, strength ${factor.toFixed(2)}, aerobic ${reentryAerobicFactor.toFixed(2)}`);

        // Retroactive e1RM decay — handles users with a plan accepted
        // BEFORE this fix shipped. Gated by e1RMsDecayed so we run once.
        // Without this, existing accepted plans would keep prescribing
        // off the stale pre-break e1RMs even though the cap is applied.
        if (uid && !acceptedPlan.e1RMsDecayed) {
          try {
            const { decayUserE1RMsForReentry } = await import('./dynamic1RM');
            const { updateDoc, doc } = await import('firebase/firestore');
            const { db: firestoreDb } = await import('./firebase');
            const decayResult = await decayUserE1RMsForReentry(uid, acceptedPlan.daysOffAtAccept);
            if (decayResult && decayResult.affectedCount > 0) {
              console.log(
                ` Reentry e1RM decay (retroactive): ${decayResult.affectedCount} lift(s) reduced to ${decayResult.retentionPct}% retained`,
              );
              await updateDoc(doc(firestoreDb, 'users', uid), {
                'acceptedReentryPlan.e1RMsDecayed': true,
                'acceptedReentryPlan.e1RMRetentionPct': decayResult.retentionPct,
              });
            } else if (decayResult) {
              // No affected lifts (decay floor not triggered or no 1RMs)
              // — still mark to skip the read on every session.
              await updateDoc(doc(firestoreDb, 'users', uid), {
                'acceptedReentryPlan.e1RMsDecayed': true,
              });
            }
          } catch (decayErr) {
            console.warn(' Reentry retroactive e1RM decay failed (non-critical):', decayErr);
          }
        }
      }
    } else if (reentryState.status === 'returning' && !programIsReentryFriendly) {
      // No accepted plan but user is detected as returning — build the
      // plan AND auto-accept it. Without auto-accept, the cap protected
      // only the FIRST comeback session (after that daysSinceLastSession
      // resets to 0 and status flips to 'on-track'). The user has to tap
      // a banner to lock in the multi-session ramp, which they don't
      // always notice mid-workout. Persisting the plan here means session
      // 2, 3, ... still read the locked ramp factor and stay protected.
      //
      // Pulled with `mode: 'freestyle'` so we DON'T touch the program day
      // pointer — the user can still rewind via the banner if they want;
      // auto-acceptance just locks the intensity cap.
      let userAge: number | undefined;
      try {
        if (uid) {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db: firestoreDb } = await import('./firebase');
          const snap = await getDoc(doc(firestoreDb, 'users', uid));
          if (snap.exists()) userAge = (snap.data() as any).age;
        }
      } catch { /* non-critical */ }
      const plan = buildReturnPlan(reentryState, userAge);
      if (plan) {
        reentryTriggered = true;
        reentryFactor = plan.week1IntensityPct / 100;
        reentryAerobicFactor = plan.week1AerobicIntensityPct / 100;
        reentryDays = reentryState.daysSinceLastSession;
        intensityAdjustment *= reentryFactor;
        caps.push(Cap.reentry(reentryFactor, 1.0, 1, plan.rampWeeks));
        status = 'reentry';
        reentryReasonShort = `REENTRY: ${reentryDays}d off — cap at ${plan.week1IntensityPct}% (auto-locked)`;
        reasoning.push(
          `REENTRY (auto-locked): ${reentryDays} days off → cap working sets at ${plan.week1IntensityPct}%, aerobic at ${plan.week1AerobicIntensityPct}% of pre-break. Locked for ${plan.rampWeeks} ramp week${plan.rampWeeks === 1 ? '' : 's'} — relaxes each week. Override via the re-entry banner.`,
        );
        console.log(` Reentry gate: AUTO-ACCEPTING plan — ${reentryDays}d off, strength ${plan.week1IntensityPct}%, aerobic ${plan.week1AerobicIntensityPct}%, ${plan.rampWeeks}wk ramp`);

        // Persist the auto-accepted plan in the background. Don't block
        // session generation on the write — the cap is already applied
        // to this session via reentryFactor above. If the write fails
        // (offline, no uid, etc.) the user just gets re-detected on the
        // next session and re-auto-accepted, which is harmless.
        if (uid) {
          try {
            const { acceptReentryPlan } = await import('./acceptedReentryPlan');
            await acceptReentryPlan(uid, plan, reentryDays, { mode: 'freestyle' });
            console.log(' Reentry plan auto-persisted — next session reads the ramp factor');

            // Decay stored e1RMs to reflect the Bosquet detraining curve.
            // Without this, recommendations would still see pre-break
            // numbers as the baseline — the reentry cap protects this
            // session only, but the underlying e1RM stays stale. Decaying
            // the baseline once means every downstream recommendation
            // (statistical, ML, profile) naturally uses the correct
            // weight automatically. Gated by e1RMsDecayed flag so we
            // never re-decay (which would spiral the baseline down).
            try {
              const { decayUserE1RMsForReentry } = await import('./dynamic1RM');
              const { updateDoc, doc } = await import('firebase/firestore');
              const { db: firestoreDb } = await import('./firebase');
              const decayResult = await decayUserE1RMsForReentry(uid, reentryDays);
              if (decayResult && decayResult.affectedCount > 0) {
                console.log(
                  ` Reentry e1RM decay applied: ${decayResult.affectedCount} lift(s) reduced to ${decayResult.retentionPct}% retained ` +
                  `(Bosquet 2013 curve for ${reentryDays}d off)`,
                );
                // Mark the plan to prevent re-decay on subsequent sessions.
                // Dotted field paths so we only touch these two fields on
                // the existing acceptedReentryPlan object (setDoc with
                // merge:true would replace the whole nested map).
                await updateDoc(doc(firestoreDb, 'users', uid), {
                  'acceptedReentryPlan.e1RMsDecayed': true,
                  'acceptedReentryPlan.e1RMRetentionPct': decayResult.retentionPct,
                });
              }
            } catch (decayErr) {
              console.warn(' Reentry e1RM decay failed (non-critical, plan still locked):', decayErr);
            }
          } catch (err) {
            console.warn(' Reentry auto-accept persist failed (non-critical, cap still applies this session):', err);
          }
        }
      }
    }
  } catch (err) {
    console.warn(' Reentry gate compute failed (non-critical):', err);
  }
  
  // ============================================================================
  // EVIDENCE-BASED ADJUSTMENT HIERARCHY
  // Ref: 2025 Network Meta-Analysis (APRE > RPE > PBRT), Banister fitness-fatigue
  // model, Delphi consensus on deloading, Gabbett ACWR sweet spot (0.8-1.3)
  // ============================================================================

  // LAYER 0: DELOAD CONTEXT GATE (checked first — skips all progression if active)
  // Per Banister model: deload is fatigue management, not load management.
  // Running progression engines during deload produces noise, not signal.
  if ((recoveryDebt.needsDeload || options.overrideRest) && !options.overrideDeload) {
    volumeAdjustment = 0.5;    // 50% volume
    intensityAdjustment *= 0.7; // 70% intensity (deload weights)
    // Batch B (2026-07 ML plan): a rest-day "train anyway" (overrideRest)
    // routes through this same deload-priority cap so the low-recovery signal
    // still lightens the session. NOT worded "identical to auto-deload" — the
    // cap is submitted to the decision table and the BINDING cap wins (a
    // returning user with reentry active at priority 1000 correctly binds
    // reentry ~0.85, not 0.70).
    const firedByRestOverride = !recoveryDebt.needsDeload && !!options.overrideRest;
    const deloadWhy = firedByRestOverride
      ? `trained through recommended rest (recovery ${recoveryScore} below floor) — routed through the deload-priority cap; binding cap wins`
      : (recoveryDebt.deloadReason || 'accumulated fatigue (RPE trend / recovery debt)');
    caps.push(Cap.deload(deloadWhy));
    deloadTriggered = true;
    status = 'deload';
    if (firedByRestOverride) {
      reasoning.push(`REST OVERRIDE: ${deloadWhy} — 50% volume, 70% intensity, progression bypassed`);
      console.log(' Smart Workout: rest-day override — lightened session via deload-priority cap');
    } else {
      reasoning.push(`AUTO-DELOAD: ${deloadWhy} — 50% volume, 70% intensity, progression bypassed`);
      console.log(' Smart Workout: AUTO-DELOAD triggered (uncoupled fatigue signals)');
    }
  }

  // Detect post-deload supercompensation opportunity
  // Delphi consensus: post-deload is opportunity to "increase training intensity"
  let postDeloadBoost = false;
  if (!deloadTriggered) {
    try {
      const recentSessions = ptSessions.slice(-5);
      const hadRecentDeload = recentSessions.some(s => (s as any).isDeload === true);
      const lastSessionWasDeload = recentSessions.length > 0 && (recentSessions[recentSessions.length - 1] as any).isDeload === true;
      // Boost if we had a deload recently but the very last session was the deload (this is the first session back)
      if (hadRecentDeload && lastSessionWasDeload) {
        postDeloadBoost = true;
        reasoning.push('Post-deload session → +3% intensity boost (supercompensation window)');
      }
    } catch { /* non-critical */ }
  }

  // LAYER 3: SAFETY GUARDRAILS (only fire at genuine extremes)
  // Per Gabbett: ACWR 0.8-1.3 is the sweet spot where adaptation happens.
  // Recovery 55-85 is normal operating range — no adjustment needed.
  if (!deloadTriggered) {
    // Recovery: only adjust at genuine extremes
    if (recoveryScore < 55) {
      intensityAdjustment *= 0.9; // -10% intensity — genuinely impaired
      caps.push(Cap.readinessLow(recoveryScore));
      reasoning.push(`Low recovery (${recoveryScore}) → -10% intensity`);
    } else if (recoveryScore >= 85) {
      intensityAdjustment *= 1.03; // +3% when fully recovered
      caps.push(Cap.readinessHigh(recoveryScore));
      reasoning.push(`High recovery (${recoveryScore}) → +3% intensity`);
    }

    // ACWR elevated: monitor but don't reduce (0.8-1.3 is optimal training zone)
    if (acwrStatus === 'elevated') {
      reasoning.push(`ACWR ${acwr.toFixed(2)} elevated → monitoring (within training zone, no reduction)`);
    }
  }

  // ============================================================================
  // ML CAPACITY BLEND — REMOVED 2026-07-14 (audit P0-1).
  //
  // Previously: if (mlCapacity.confidence >= 0.7) intensityAdjustment =
  //   intensityAdjustment * 0.3 + (mlCapacity.optimalLoad / 100) * 0.7
  //
  // Three independent defects made this a silent, unconditional load haircut:
  //
  //  1. The model's training LABEL is invalid. mlUserProfiling's
  //     calculateSessionPerformance = (totalVolume/100) × (10 − avgRPE), capped
  //     at 100 — so any session over ~5,000 lb at RPE ≤8 saturates at 100, and
  //     the label PENALIZES effort (higher RPE = "worse performance"), inverting
  //     the premise of progressive overload.
  //  2. Its `confidence` is a hardcoded literal (`confidence: 0.8`) — no accuracy
  //     is ever computed. That fake 0.8 always cleared this ≥0.7 gate, so the
  //     blend fired for EVERY user with 30+ sessions.
  //  3. Readiness was already double-counted inside it: optimalLoad =
  //     predictedPerformance × readinessScore, while readiness is ALSO a model
  //     input feature and ALSO scales the prescription elsewhere.
  //
  // Net arithmetic: saturated performance (~100) × readiness 80 → optimalLoad 80
  // → blended intensity = 0.3×1.0 + 0.7×0.80 = 0.86 ON A FULLY NORMAL DAY.
  // At the no-data readiness default of 70 it was 0.79. A silent 14-21% cut
  // applied precisely to the most data-rich users — the single largest cause of
  // the reported systematic under-prescription.
  //
  // 2026-07 ML plan Batch A finished the job: the mlCapacity computation, its
  // reasoning line, and the write-only `intelligence.mlCapacity` payload are
  // deleted outright. Re-introducing any of it requires a real train/test
  // accuracy and a label that isn't volume-saturated.
  // ============================================================================

  // Consecutive training day fatigue: only at 5+ days (3 consecutive is normal for most programs)
  const consecutiveTrainingDays = calculateConsecutiveTrainingDays(ptSessions);
  if (consecutiveTrainingDays >= 6) {
    volumeAdjustment *= 0.85;
    caps.push(Cap.consecutiveDays(consecutiveTrainingDays));
    reasoning.push(`${consecutiveTrainingDays} consecutive training days → -15% volume`);
  } else if (consecutiveTrainingDays >= 5) {
    volumeAdjustment *= 0.9;
    caps.push(Cap.consecutiveDays(consecutiveTrainingDays));
    reasoning.push(`${consecutiveTrainingDays} consecutive training days → -10% volume`);
  }
  
  // NUTRITION-AWARE VOLUME ADJUSTMENT — DISABLED 2026-07-07.
  // Nutrition tracking isn't reliable enough to gate training volume: when a
  // user stops logging food, the analysis reads stale/sparse data and silently
  // trims sets ("low volume for no apparent reason" — reported by a
  // well-recovered user). Cutting volume for a normal fat-loss deficit also
  // surprised users who intentionally diet. Removed from the generator until
  // nutrition data is trustworthy AND the cut is clearly surfaced + overrideable.
  // (Prior logic: >500 kcal/day deficit or <0.7 g/lb protein over 3+ tracked
  // days → -10%/-15% volume via getNutritionAnalysis + Cap.nutritionLow.)

  // ────────────────────────────────────────────────────────────────────
  // DECISION TABLE RESOLUTION
  // ────────────────────────────────────────────────────────────────────
  // Up to this point intensityAdjustment / volumeAdjustment have been
  // composed via legacy multiplicative stacking — each engine multiplied
  // its scalar into the running value. That model silently compounded
  // caps: reentry (0.85) × deload (0.7) × low recovery (0.9) × consec
  // days (0.85) = 0.46 even though no single engine wanted to take the
  // user below ~70%. The decision table replaces this with priority-
  // based binding-constraint selection — exactly one cap binds per axis
  // and the others surface in reasoning as "suppressed by …".
  //
  // The legacy mutations above still run so engine-specific side
  // effects (deloadTriggered, status, reasoning) keep working. The
  // decision-table output OVERRIDES the running intensityAdjustment /
  // volumeAdjustment values. ML capacity blend and synthesis still
  // stack ON TOP of the binding cap intentionally — those are
  // personalized fine-tunes, not caps.
  if (caps.length > 0) {
    const decision = decideIntensity(caps);
    const before = `${intensityAdjustment.toFixed(3)} × ${volumeAdjustment.toFixed(3)}`;
    intensityAdjustment = decision.intensity;
    volumeAdjustment = decision.volume;
    reasoning.push(`[DECISION TABLE] ${before} → ${decision.intensity.toFixed(3)} × ${decision.volume.toFixed(3)} (binding intensity: ${decision.bindingIntensity}, binding volume: ${decision.bindingVolume})`);
    for (const r of decision.reasons) reasoning.push(r);
    console.log(`[decision-table] caps=${caps.length} binding=${decision.bindingIntensity}/${decision.bindingVolume} intensity=${decision.intensity.toFixed(3)} volume=${decision.volume.toFixed(3)}`);
  }

  // SYNTHESIS-DRIVEN ADJUSTMENTS (from metrics page intelligence)
  // Gated behind minimum session count to avoid premature pattern detection
  let synthesisContext: SmartWorkoutResult['synthesisContext'] = undefined;
  try {
    const sessionCount = await db.sessions.where('userId').equals(userId || '').count();
    if (sessionCount < 10) {
      console.log(`[Smart] Skipping synthesis — only ${sessionCount} sessions (need 10+)`);
      reasoning.push(`Synthesis skipped: ${sessionCount}/10 sessions needed`);
    } else {
      const { loadSynthesisModifiers } = await import('./synthesisWorkoutBridge');
      const synthMods = await loadSynthesisModifiers();
      if (synthMods && synthMods.activeSyntheses.length > 0) {
        // Attenuate synthesis multipliers by confidence (0-1).
        // Low confidence → reductions are dampened toward 1.0 (no effect).
        // Below 0.4 confidence, synthesis is skipped entirely.
        const confidence = synthMods.confidence ?? 1.0;
        if (confidence < 0.4) {
          reasoning.push(`Synthesis skipped: confidence too low (${(confidence * 100).toFixed(0)}%)`);
        } else {
          // Lerp multipliers toward 1.0 based on confidence:
          //   effective = 1.0 + (raw - 1.0) * confidence
          const attenuatedVol = 1.0 + (synthMods.volumeMultiplier - 1.0) * confidence;
          const attenuatedInt = 1.0 + (synthMods.intensityMultiplier - 1.0) * confidence;
          volumeAdjustment *= attenuatedVol;
          intensityAdjustment *= attenuatedInt;
          for (const s of synthMods.activeSyntheses) {
            reasoning.push(`[INTEL] ${s.title}`);
          }
          if (confidence < 0.8) {
            reasoning.push(`[INTEL] Confidence ${(confidence * 100).toFixed(0)}% — synthesis effects attenuated`);
          }
          synthesisContext = {
            activeSyntheses: synthMods.activeSyntheses,
            volumeMultiplier: attenuatedVol,
            intensityMultiplier: attenuatedInt,
            exerciseFlags: synthMods.exerciseFlags,
          };
        }
      }
    }
  } catch { /* non-critical */ }

  // ============================================================================
  // SAFETY FLOOR + REENTRY BINDING-CONSTRAINT CLAMP
  // ============================================================================
  // Multiple ML signals (reentry, deload, recovery, ACWR, synthesis) all
  // multiply `intensityAdjustment`. Naive stacking produces absurd
  // reductions (e.g. reentry 0.7 × deload 0.7 × recovery 0.9 = 0.44 →
  // workout drops below minimal effective dose).
  //
  // Two-tier safety:
  //   1. When reentry is ACTIVE, the reentry factor is the binding signal.
  //      Other ML can fine-tune the load within ±15% of the cap, but cannot
  //      push outside [reentry × 0.85, reentry]. The reentry block already
  //      multiplied intensityAdjustment by reentryFactor at the top, so
  //      any subsequent multipliers (deload, recovery, synthesis) get
  //      clamped here. Hard floor at 40% to keep some training stimulus.
  //   2. When reentry is INACTIVE, fall back to the legacy floor (intensity
  //      ≥70%, volume ≥50%) — the original anti-cascade guard.
  if (reentryTriggered) {
    const reentryFloor = Math.max(0.40, reentryFactor * 0.85);
    const reentryCeil = reentryFactor;
    const clamped = Math.max(reentryFloor, Math.min(reentryCeil, intensityAdjustment));
    if (clamped !== intensityAdjustment) {
      console.log(`[Smart] Reentry clamp: ${intensityAdjustment.toFixed(2)} → ${clamped.toFixed(2)} (band ${reentryFloor.toFixed(2)}-${reentryCeil.toFixed(2)})`);
      reasoning.push(
        `Reentry-band clamp: ${Math.round(intensityAdjustment * 100)}% from stacked ML signals adjusted to ${Math.round(clamped * 100)}% (band ${Math.round(reentryFloor * 100)}-${Math.round(reentryCeil * 100)}%). Reentry is the binding constraint during the ramp.`,
      );
      intensityAdjustment = clamped;
    }
    // Volume can still be reduced normally — reentry doesn't gate set count.
    volumeAdjustment = Math.max(volumeAdjustment, 0.5);
  } else {
    volumeAdjustment = Math.max(volumeAdjustment, 0.5);
    intensityAdjustment = Math.max(intensityAdjustment, 0.7);
  }

  // DELOAD OVERRIDE: When program has a built-in deload week, skip stacking
  // other reductions — the template already accounts for deload weights.
  // BUT: if reentry is active, the reentry cap still applies (a returning
  // user on a programmed deload week should still see reentry-capped loads,
  // not full programmed loads).
  /**
   * A PROGRAMMED deload week.
   *
   * This read `(program as any).schedule[week - 1].isDeload` — a field NO
   * program defines. `schedule` does not exist on WorkoutProgram or on any
   * of the 20+ program files, so `programSchedule` was always undefined and
   * `isDeloadWeek` was ALWAYS FALSE. The scheduled-deload path has therefore
   * never executed once, which is why deload fired 0 times in 244 eligible
   * days: the reactive trigger in recoveryDebt.ts is strict by design, and
   * the scheduled one — the path that is supposed to do the routine work —
   * was reading a phantom field through an `as any`.
   *
   * Programs express deloads through their own periodization model:
   *   { weeks: [7], type: 'deload', intensityMultiplier: 0.85, ... }
   * (see lib/programs/gruntProgram12Week.ts:64). Read that.
   */
  const programPhases = program.periodization?.phases;
  const deloadPhase = programPhases?.find(
    ph => ph.type === 'deload' && Array.isArray(ph.weeks) && ph.weeks.includes(week),
  );
  const isDeloadWeek = !!deloadPhase;
  if (isDeloadWeek) {
    console.log(` Programmed deload week ${week}: "${deloadPhase!.name}" (intensity x${deloadPhase!.intensityMultiplier}, volume x${deloadPhase!.volumeMultiplier})`);
  }
  if (isDeloadWeek) {
    if (reentryTriggered) {
      // Reset other reductions but keep reentry as the binding cap.
      volumeAdjustment = 1.0;
      intensityAdjustment = reentryFactor;
      deloadTriggered = false;
      reasoning.push(`Program deload week — using reentry cap (${Math.round(reentryFactor * 100)}%) instead of stacking ACWR/synthesis reductions.`);
      console.log('[Smart] Deload week + reentry — using reentry cap');
    } else {
      volumeAdjustment = 1.0;
      intensityAdjustment = 1.0;
      deloadTriggered = false;
      status = 'ready';
      reasoning.push('Program deload week — training at programmed weights (no stacked reductions)');
      console.log('[Smart] Program deload week active — skipping ACWR/synthesis reductions');
    }
  }

  // ============================================================================
  // STEP 5: CHECK FOR PROGRAM COMPLETION
  // ============================================================================

  const programDuration = program.metadata.duration || 12;
  if (week > programDuration) {
    console.log(` Smart Workout: Program complete! Week ${week} exceeds duration ${programDuration}`);
    return {
      status: 'program-complete',
      workout: null,
      reasoning: [...reasoning, `Congratulations! You have completed this ${programDuration}-week program!`],
      intelligence: {
        recoveryScore,
        acwr,
        acwrStatus,
        trainingLevel,
        mlReady,
        deloadTriggered,
        volumeAdjustment,
        intensityAdjustment,
      },
      adjustments: [],
      substitutions: [],
      synthesisContext,
      message: `Congratulations! You've completed the ${program.metadata.name} program! Time to select your next challenge or repeat this program.`,
    };
  }

  // ============================================================================
  // STEP 6: GENERATE BASE WORKOUT FROM TEMPLATE
  // ============================================================================

  const template = getWorkoutTemplate(program, week, day);

  // Passive rest = no template at all OR a template with no main work
  // and no recovery content. (Active rest — a template carrying a
  // mobility/recovery block — falls through to normal generation so the
  // user gets the light session.) Classifying here makes rest detection
  // robust whether a rest day is modeled as a null template or an empty
  // one, instead of only catching the null case.
  const isPassiveRest = !template || classifyDayType(template) === 'passive-rest';

  if (isPassiveRest) {
    console.log(' Smart Workout: passive rest day (no main work scheduled)');
    // Scheduled rest day — record so Spearhead doesn't penalize
    if (userId) {
      import('./spearheadEngine').then(m => m.recordSanctionedRestDay(userId!)).catch(() => {});
    }
    return {
      status: 'rest',
      workout: null,
      reasoning: [...reasoning, `No template found for Week ${week}, Day ${day}`],
      intelligence: {
        recoveryScore,
        acwr,
        acwrStatus,
        trainingLevel,
        mlReady,
        deloadTriggered,
        volumeAdjustment,
        intensityAdjustment,
      },
      adjustments: [],
      substitutions: [],
      synthesisContext,
      message: `Rest day. No workout scheduled for Week ${week}, Day ${day}.`,
    };
  }
  
  // Generate base workout with periodization.
  //
  // INTENSITY IS DELIBERATELY *NOT* APPLIED HERE (audit P0-2/P0-3, 2026-07-14).
  // We pass 1.0 so the template produces a FULL-INTENSITY baseline load, and the
  // session multiplier (deload / readiness / reentry / synthesis) is applied
  // EXACTLY ONCE at the end of the per-exercise chain ("SINGLE SESSION INTENSITY
  // MULTIPLIER" below).
  //
  // Why: intensityAdjustment used to be baked in here, but only for lifts whose
  // load comes from the template's %1RM math. A lift that instead resolved to the
  // e1RM anchor, to its last-used weight, or to a first-time suggestion NEVER got
  // it — while the deload gate, the reentry ceiling and the statistical engine's
  // readinessMultiplier each re-applied it on top. Same scalar, three
  // inconsistent application points, and a different number of applications
  // depending on which baseline a lift happened to resolve to:
  //   • deload:  template ×0.7 THEN per-exercise ×0.7  → 49% of programmed load
  //   • reentry: template ×factor THEN ceiling ×factor → factor² for %-based lifts
  //   • readiness: template ×r THEN statistical ×r     → r² on low-history lifts
  // Making the baseline unmodulated is what lets all of those collapse to one
  // honest multiplication.
  //
  // VOLUME still flows through here, because generateWorkout is the only layer
  // that can scale cardio/duration-based set counts; the per-exercise P4 step
  // therefore no longer re-scales by volumeAdjustment (it only does the RPE-based
  // ±1 set nudge).
  const baseWorkout = generateWorkout(
    template,
    program,
    week,
    oneRMs,
    {
      volumeMultiplier: volumeAdjustment,
      intensityMultiplier: 1.0,
    }
  );
  
  // ============================================================================
  // STEP 6: APPLY INTELLIGENT LOAD RECOMMENDATIONS TO EACH EXERCISE
  //
  // Simplified 3-tier Priority Chain:
  // P0. AUTO-PROGRESS (session-based progression decisions)
  // P1. STATISTICAL (trend/RPE-based) - Simple, interpretable, works with small data
  // P2. HYBRID RULES - Fallback when no data
  // ============================================================================

  // Build exercise order for movement pattern fatigue calculation
  const exerciseOrder = baseWorkout.map(ex => {
    const setsMatch = ex.sets_reps.match(/^(\d+)/);
    return { name: ex.name, sets: setsMatch ? parseInt(setsMatch[1]) : 3 };
  });

  // Fix 7: Build exercise flag map from synthesis context for per-exercise consumption
  const exerciseFlagMap = new Map<string, Array<{ flag: string; detail: string }>>();
  if (synthesisContext?.exerciseFlags) {
    for (const ef of synthesisContext.exerciseFlags) {
      const key = ef.exerciseName.toLowerCase();
      if (!exerciseFlagMap.has(key)) exerciseFlagMap.set(key, []);
      exerciseFlagMap.get(key)!.push({ flag: ef.flag, detail: ef.detail });
    }
  }

  // Process all exercises in PARALLEL instead of sequentially.
  // Each exercise goes through the priority chain independently.
  const enhancedResults = await Promise.all(baseWorkout.map(async (exercise, exerciseIndex) => {
    let originalLoad = exercise.target_load;
    let finalLoad = originalLoad;
    let adjustmentReason = '';
    let adjustmentSource: ExerciseAdjustment['source'] = 'rule';
    let statisticalData: ExerciseAdjustment['statistical'] | undefined;
    let plateauData: SimplePlateauResult | undefined;
    /**
     * Load immediately BEFORE a plateau trim was applied, if one was.
     * The demonstrated-capacity floor further down can restore the weight to a
     * proven value at or above this — in which case the trim never actually
     * happened and must not be reported as though it did. Observed in the wild:
     * "Plateau reload: 5 sessions stalled → -10% (rebuild phase)" shown next to
     * "Held at 75 lb", with the prescription unchanged at 75 for three sessions
     * running. Telling an athlete they're being deloaded when they aren't
     * corrodes trust in every other explanation the coach gives.
     */
    let plateauTrimFromLoad: number | null = null;
    // Always undefined since the pattern-fatigue trim was removed (see LAYER 2).
    // Kept so the ExerciseAdjustment shape stays stable for existing consumers.
    const patternFatigueData: { fatigueMultiplier: number; reason: string } | undefined = undefined;

    // Track ALL adjustment sources for transparency (the adjustment chain)
    const adjustmentSources: AdjustmentSource[] = [];

    // ========================================================================
    // ASSESSMENT / MAX-TESTING GATE
    //
    // 1RM / 8RM / 5RM test days are RPE 10 self-regulating efforts — the
    // POINT is to discover the user's current max, not have the system
    // prescribe a number. Applying reentry caps, recovery reductions, or
    // ML progression here is nonsensical:
    //   - originalLoad on testing exercises is usually a placeholder (45 lb
    //     bar), not a baseline
    //   - "Reduce 45 → 30 lbs" makes no sense for a max test
    //   - The user knows their RPE 10. Trust them to find the weight.
    //
    // When detected, we leave the exercise's target_load as-is (or
    // bump it to the user's known 1RM for reference) and push NO
    // adjustment record so the banner stays clean.
    // ========================================================================
    const isAssessmentExercise =
      (exercise as any).sessionType === 'assessment' ||
      /\b(?:\d+\s*)?(?:RM|max\s*effort|max\s*lift|to\s*failure)\b/i.test(exercise.sets_reps || '') ||
      /\b(?:\d+\s*)?(?:RM|max)\b/i.test(exercise.intensity || '') ||
      /(?:max\s*test|testing|assessment)/i.test((exercise as any).block || '');

    if (isAssessmentExercise) {
      // Use the robust lookup so duplicate-cased 1RM records (e.g.
      // `backSquat` AND `backsquat` AND `Back Squat`) all resolve.
      const knownMax = lookup1RM(oneRMs, exercise.name);
      if (knownMax && knownMax > 0 && (originalLoad <= 45 || knownMax > originalLoad * 1.5)) {
        exercise.target_load = knownMax;
        console.log(` Assessment: ${exercise.name} | bumping placeholder ${originalLoad} → ${knownMax} (known 1RM)`);
      }
      // No adjustment record — banner stays clean for assessment exercises.
      return { exercise, adjustment: null };
    }

    // ========================================================================
    // CIRCUIT EXERCISES: Apply circuit-specific ML recommendations
    // ========================================================================
    const formatLower = (exercise.format || '').toLowerCase();
    const isCircuitExercise = formatLower.includes('circuit') ||
                              formatLower.includes('amrap') ||
                              formatLower.includes('emom') ||
                              formatLower.includes('for time') ||
                              formatLower.includes('grinder') ||
                              formatLower.includes('hiit');

    if (isCircuitExercise) {
      try {
        // Get circuit ML recommendation
        const repsMatch = exercise.sets_reps.match(/(\d+)\s*(?:rounds?|x)/i);
        const currentTargetRounds = repsMatch ? parseInt(repsMatch[1]) : 5;

        const circuitRec = await getCircuitRecommendation(exercise.name, currentTargetRounds);

        // Reentry aerobic cap — VO2max detrains ~2× faster than 1RM, so we
        // attenuate cardio/circuit rounds during the ramp. Pace target stays
        // the same (don't push pace, don't push volume).
        const aerobicCapRounds = reentryTriggered && reentryAerobicFactor < 1.0
          ? Math.max(1, Math.round(currentTargetRounds * reentryAerobicFactor))
          : null;

        // Build adjustment based on circuit analysis
        if (circuitRec.adjustment.type !== 'maintain' && circuitRec.adjustment.suggestedRounds) {
          let finalRounds = circuitRec.adjustment.suggestedRounds;
          let cappedReason = circuitRec.adjustment.reason;
          if (aerobicCapRounds !== null && finalRounds > aerobicCapRounds) {
            cappedReason = `Reentry aerobic cap: ${aerobicCapRounds} rounds (${Math.round(reentryAerobicFactor * 100)}%) overrides ML suggestion of ${finalRounds}`;
            finalRounds = aerobicCapRounds;
          }
          const roundDiff = finalRounds - currentTargetRounds;
          const direction = roundDiff > 0 ? 'increase' : 'decrease';

          console.log(` Circuit ML: ${exercise.name} | ${currentTargetRounds} → ${finalRounds} rounds (${cappedReason})`);

          return {
            exercise: {
              ...exercise,
              // Update sets_reps with new round count
              sets_reps: exercise.sets_reps.replace(/\d+(?=\s*(?:rounds?|x))/i, finalRounds.toString()),
            },
            adjustment: {
              exerciseName: exercise.name,
              originalLoad,
              adjustedLoad: originalLoad,  // No weight adjustment for circuits
              adjustmentPercent: 0,
              source: 'statistics' as const,
              reason: cappedReason,
              circuitData: {
                paceTargetSeconds: circuitRec.targetPace.roundTimeSeconds,
                paceTargetDisplay: circuitRec.targetPace.displayTime,
                paceDegradation: circuitRec.metrics.avgPaceDegradation,
                consistencyScore: circuitRec.metrics.consistencyScore,
                trend: circuitRec.metrics.trend,
                sprintTarget: circuitRec.sprintRecommendation ? {
                  timeSeconds: circuitRec.sprintRecommendation.targetTimeSeconds,
                  display: circuitRec.sprintRecommendation.displayTime,
                  message: circuitRec.sprintRecommendation.message,
                } : undefined,
              },
            },
          };
        } else if (aerobicCapRounds !== null && currentTargetRounds > aerobicCapRounds) {
          // ML says maintain, but reentry aerobic cap overrides downward.
          console.log(` Circuit reentry-cap: ${exercise.name} | ${currentTargetRounds} → ${aerobicCapRounds} rounds (aerobic cap ${Math.round(reentryAerobicFactor * 100)}%)`);
          return {
            exercise: {
              ...exercise,
              sets_reps: exercise.sets_reps.replace(/\d+(?=\s*(?:rounds?|x))/i, aerobicCapRounds.toString()),
            },
            adjustment: {
              exerciseName: exercise.name,
              originalLoad,
              adjustedLoad: originalLoad,
              adjustmentPercent: 0,
              source: 'statistics' as const,
              reason: `Reentry aerobic cap: ${aerobicCapRounds} rounds (${Math.round(reentryAerobicFactor * 100)}% of pre-break volume).`,
              circuitData: {
                paceTargetSeconds: circuitRec.targetPace.roundTimeSeconds,
                paceTargetDisplay: circuitRec.targetPace.displayTime,
                paceDegradation: circuitRec.metrics.avgPaceDegradation,
                consistencyScore: circuitRec.metrics.consistencyScore,
                trend: circuitRec.metrics.trend,
                sprintTarget: undefined,
              },
            },
          };
        } else {
          // Maintain current volume but still return circuit data for UI
          console.log(` Circuit ML: ${exercise.name} | Maintaining ${currentTargetRounds} rounds (${circuitRec.adjustment.reason})`);

          return {
            exercise,
            adjustment: circuitRec.metrics.sessionCount > 0 ? {
              exerciseName: exercise.name,
              originalLoad,
              adjustedLoad: originalLoad,
              adjustmentPercent: 0,
              source: 'statistics' as const,
              reason: circuitRec.adjustment.reason,
              circuitData: {
                paceTargetSeconds: circuitRec.targetPace.roundTimeSeconds,
                paceTargetDisplay: circuitRec.targetPace.displayTime,
                paceDegradation: circuitRec.metrics.avgPaceDegradation,
                consistencyScore: circuitRec.metrics.consistencyScore,
                trend: circuitRec.metrics.trend,
                sprintTarget: circuitRec.sprintRecommendation ? {
                  timeSeconds: circuitRec.sprintRecommendation.targetTimeSeconds,
                  display: circuitRec.sprintRecommendation.displayTime,
                  message: circuitRec.sprintRecommendation.message,
                } : undefined,
              },
            } : null,
          };
        }
      } catch (error) {
        console.log(` Circuit ML failed for ${exercise.name}:`, error);
        return { exercise, adjustment: null };
      }
    }

    // Skip explicit non-strength exercises (cardio, ruck, bodyweight)
    // BUT allow loaded conditioning (sled, carries) to get weight recommendations.
    const lowerName = exercise.name.toLowerCase();
    const isLoadedConditioning = lowerName.includes('sled') || lowerName.includes('drag') ||
                                  lowerName.includes('carry') || lowerName.includes('farmer') ||
                                  lowerName.includes('kettlebell');
    const isCardio = exercise.format === 'cardio' || exercise.format === 'ruck';

    const isCarryExercise = isCarryPrescription(exercise.name, exercise.format);
    // Timed holds progress on DURATION, the way carries progress on distance.
    // `timed` is the third-largest tracking type in production (165 logs) and
    // had no adaptive path at all — a plank sat at its authored duration
    // forever, however easy it got.
    const isTimedHold =
      getTrackingType(exercise.name) === 'time' ||
      /^(timed|time)$/i.test(exercise.format || '') ||
      /\bplank\b|hollow hold|wall sit|dead ?hang|\bhold\b|isometric/i.test(lowerName);
    /** Unit noun for user-facing copy about a carry's `reps` field. */
    const repUnitLabel = isCarryExercise ? 'm' : 'reps';
    // Bodyweight detection: prefer the curated DB trackingType over the
    // authored intensity string, and compare that string case-insensitively —
    // lib/programs/ authors both 'bodyweight' (19 sites) and 'Bodyweight'
    // (5 sites), and the old capital-B-only compare sent lowercase-authored
    // push-ups down the strength path to a fabricated 15/25/40 lb suggestion.
    // Load-plus-bodyweight lifts (weighted pull-ups/dips) are excepted from
    // the trackingType check so loaded progression on them keeps working when
    // a program authors them as loaded lifts.
    const isNonStrength = !isLoadedConditioning && (
                          (exercise.trackingType === 'bodyweight-reps' &&
                            !isLoadPlusBodyweightLift(exercise.name)) ||
                          exercise.format === 'cardio' ||
                          exercise.format === 'ruck' ||
                          exercise.format === 'bodyweight' ||
                          (exercise.intensity || '').toLowerCase() === 'bodyweight' ||
                          lowerName.includes('stretch') ||
                          lowerName.includes('warm-up') ||
                          lowerName.includes('mobility'));

    if (isNonStrength) {
      // Wire the recovery-aware cardio/ruck engine (adaptiveCardioPace) into
      // session generation. Cardio has no "weight" lever, so on a low-recovery
      // day or a programmed deload week we trim the distance/duration (and drop
      // treadmill incline) instead — the same intent the strength deload gate
      // has for loaded lifts. Reentry folds in as an aerobic-cap note. When the
      // engine has no basis to cut (too little history, or the athlete is
      // cleared to hold/progress) this returns null and the card is untouched.
      const cardioAdj = await computeCardioRecoveryAdjustment(
        exercise,
        recoveryScore,
        periodizationPhase,
        reentryTriggered,
        reentryAerobicFactor,
      );
      if (cardioAdj) {
        const mult = 1 + cardioAdj.adjustmentPercent / 100;
        // Attach a full AppliedAdjustment so the workout card renders the
        // change in its coach panel AND the user can revert to the program's
        // original prescription (baselineSetsReps / baselineDistance).
        const applied: AppliedAdjustment = {
          applied: cardioAdj.adjustmentPercent !== 0 || cardioAdj.targetIncline !== undefined,
          baselineLoad: 0,
          baselineDistance: parseMetersFromText(exercise.sets_reps),
          baselineIncline: exercise.targetIncline,
          baselineSetsReps: exercise.sets_reps,
          sources: [{ type: 'recovery', multiplier: mult, reason: cardioAdj.reason }],
          totalMultiplier: mult,
          explanation: cardioAdj.reason,
        };
        const updated: GeneratedExercise = {
          ...exercise,
          sets_reps: cardioAdj.setsReps,
          details: [exercise.details, cardioAdj.reason].filter(Boolean).join(' '),
          adjustment: applied,
        };
        if (cardioAdj.targetDistance !== undefined) updated.targetDistance = cardioAdj.targetDistance;
        if (cardioAdj.targetIncline !== undefined) updated.targetIncline = cardioAdj.targetIncline;
        console.log(`[cardio] recovery scaling: ${exercise.name} | ${exercise.sets_reps} -> ${cardioAdj.setsReps} (${cardioAdj.adjustmentPercent}%)`);
        return {
          exercise: updated,
          adjustment: {
            exerciseName: exercise.name,
            originalLoad: 0,
            adjustedLoad: 0,
            adjustmentPercent: cardioAdj.adjustmentPercent,
            reason: cardioAdj.reason,
            source: 'recovery' as const,
          },
        };
      }
      return { exercise, adjustment: null };
    }

    // ========================================================================
    // P1: 1RM ANCHOR — point of truth for primary lifts
    //
    // For a recognized primary barbell lift prescribed by RPE/RIR (NOT an
    // explicit %, which already encodes the 1RM via calculateTargetLoad), derive
    // the working weight from the user's CURRENT e1RM through the RPE→%1RM
    // table. The e1RM auto-updates from logged sets post-session, so this is a
    // self-correcting anchor (Speediance-style) rather than a static template
    // number. We do this BEFORE the last-used-weight fallback so a primary lift
    // tracks the user's true strength, not whatever they happened to lift last.
    // ========================================================================
    if (isPrimaryLiftWithE1RM(exercise, oneRMs) && !hasExplicitPercent(exercise.intensity)) {
      const e1RM = lookup1RM(oneRMs, exercise.name);
      const anchored = deriveWorkingLoadFrom1RM(e1RM, exercise);
      if (anchored > 0 && anchored !== originalLoad) {
        originalLoad = anchored;
        finalLoad = anchored;
        exercise.target_load = anchored;
        adjustmentReason = `Anchored to e1RM ${e1RM} lb`;
        adjustmentSource = 'rule';
        adjustmentSources.push({
          type: 'rule',
          multiplier: 1.0,
          reason: `Anchored to e1RM ${e1RM} lb @ ${exercise.intensity || 'target effort'} (working ${anchored} lb)`,
        });
        console.log(` P1 e1RM anchor: ${exercise.name} | e1RM ${e1RM} → ${anchored} lbs (${exercise.intensity})`);
      }
    }

    // If originalLoad is 0 (no 1RM), use last session's weight as baseline
    // and run the FULL progression pipeline on it. This gives carries, sleds,
    // curls, lateral raises etc. the same smart progression as 1RM exercises.
    if (!originalLoad || originalLoad <= 0) {
      try {
        const { getExerciseHistory } = await import('./statisticalRecommendation');
        const history = await getExerciseHistory(exercise.name);
        if (history && history.length > 0) {
          const lastWeight = history[history.length - 1].weight;
          if (lastWeight > 0) {
            originalLoad = lastWeight;
            finalLoad = lastWeight;
            console.log(` No 1RM for ${exercise.name} — using last-used weight ${lastWeight} lbs as baseline for progression`);
          }
        }
      } catch { /* non-critical */ }

      // INTELLIGENT FIRST-TIME WEIGHT SUGGESTION
      // If still no load (never done this exercise), suggest a conservative
      // starting weight based on exercise category and movement pattern.
      if (!originalLoad || originalLoad <= 0) {
        /**
         * Only movements that CARRY A LOAD get a starting-weight suggestion.
         *
         * Seen in the QA clickthrough: an Assault Bike in an intervals block
         * displayed "First-time suggestion: 15 lbs based on beginner level"
         * next to a DISTANCE and TIME input. There is no weight on an assault
         * bike, and the number was both meaningless and unenterable.
         *
         * The audited DB tracking type is the authority: distance-time, time,
         * swim-sets and interval work record no load. Carries and rucks DO
         * (that is the whole point of a carry), so they keep the suggestion.
         */
        const tt = getTrackingType(exercise.name);
        const carriesNoLoad = tt === 'distance-time' || tt === 'time'
          || tt === 'swim-sets' || tt === 'interval';
        const suggestedWeight = carriesNoLoad
          ? 0
          : getIntelligentStartingWeight(exercise, oneRMs, trainingLevel);
        if (suggestedWeight > 0) {
          originalLoad = suggestedWeight;
          finalLoad = suggestedWeight;
          adjustmentReason = `First-time suggestion: ${suggestedWeight} lbs (${trainingLevel} level)`;
          adjustmentSource = 'first-time-suggestion';
          adjustmentSources.push({
            type: 'rule',
            multiplier: 1.0,
            reason: `First-time suggestion: ${suggestedWeight} lbs based on ${trainingLevel} level`,
          });
          // Update the exercise's target_load so it shows in the UI
          exercise.target_load = suggestedWeight;
          console.log(` First-time suggestion for ${exercise.name}: ${suggestedWeight} lbs (${trainingLevel})`);
        } else {
          return { exercise, adjustment: null };
        }
      }
    }

    // Parse target reps
    const repsMatch = exercise.sets_reps.match(/\d+\s*[x×]\s*(\d+)/i);
    const targetReps = repsMatch ? parseInt(repsMatch[1]) : 10;

    // ========================================================================
    // EVIDENCE-BASED PER-EXERCISE ADJUSTMENT CHAIN
    // Ref: 2025 Meta-Analysis (APRE > RPE > PBRT), Banister model, Gabbett ACWR
    // ========================================================================

    // LAYER 0: DELOAD GATE — skip all progression, use programmed deload weights
    // Per Banister: deload is fatigue management. Progression signals are noise here.
    if (deloadTriggered && originalLoad > 0) {
      // Hold the baseline and bypass progression. The -30% deload cut is NOT
      // applied here — it lives in intensityAdjustment (Cap.deload → 0.70) and is
      // applied once by the SINGLE SESSION INTENSITY MULTIPLIER below.
      //
      // This line used to be `originalLoad * 0.7` while the template load had
      // ALREADY been scaled by the same 0.70, so deloads actually ran at
      // 0.7 × 0.7 = 49% of programmed load. That is not a deload — it's a wasted
      // week (and progression analysis skips deload sessions, so it was
      // informationally dead too). Delphi consensus (Bell et al. 2022/2023) puts
      // a deload near 70% intensity / 50% volume, which is what we now produce.
      finalLoad = originalLoad;
      adjustmentReason = 'DELOAD: Programmed recovery — progression bypassed';
      adjustmentSource = 'deload';
      adjustmentSources.push({
        type: 'deload',
        multiplier: 0.7,
        reason: 'Deload: -30% intensity for recovery (all progression bypassed)',
      });
      console.log(`⬇ Deload gate: ${exercise.name} | baseline ${originalLoad} lbs (session multiplier applies the -30%)`);
      // Skip to safety floor — no P0/P1/P2, no fatigue, no plateau
    } else {
      // NOTE (reentry): unlike deload, reentry NO LONGER bypasses the engine.
      // The full P0/P1/plateau/fatigue chain runs so the ramp autoregulates
      // from the user's actual reentry sessions — then a reentry CEILING is
      // applied after the safety floor (search "Reentry ceiling") so the smart
      // load can never exceed the detraining-safe cap. Brain on, bounded.
      // ====================================================================
      // LAYER 1: PROGRESSIVE OVERLOAD ENGINE (the growth driver)
      // Run BOTH P0 and P1, merge by confidence. 2025 meta-analysis shows
      // multi-session data (P1) is more reliable than single-session RPE (P0).
      // ====================================================================

      // P0: Auto-Progress (last session's RPE/rep analysis)
      let p0Result: { weight: number; applied: boolean; reason?: string; confidence?: string } | null = null;
      try {
        p0Result = await applyProgression(exercise.name, originalLoad);
        if (p0Result.applied && p0Result.weight !== originalLoad) {
          console.log(` P0 Auto-Progress: ${exercise.name} | ${originalLoad} → ${p0Result.weight} lbs (${p0Result.reason})`);
        } else {
          p0Result = null; // Not applicable
        }
      } catch (error) {
        console.log(` P0 Auto-progress check failed for ${exercise.name}:`, error);
      }

      // P1: Statistical trend (multi-session regression — conceptually closer to APRE)
      let p1Result: { weight: number; confidence: number; method: string; reasoning: string; dataPoints?: number; trend?: any } | null = null;
      try {
        // readinessMultiplier = 1.0 (audit P0-3). The statistical engine used to
        // receive intensityAdjustment here and multiply its own output by it —
        // but the baseline we hand it (originalLoad) was ALREADY scaled by the
        // same scalar upstream, so readiness was squared (0.9 → 0.81) on any lift
        // whose recommendation came from the low-history / last-known-weight
        // paths. The session multiplier is applied once, at the end.
        const statsResult = await getStatisticalRecommendation(exercise.name, targetReps, originalLoad, 1.0);
        if (statsResult && statsResult.confidence >= 0.5 && statsResult.method !== 'program-weight') {
          p1Result = statsResult;
          console.log(` P1 Statistical: ${exercise.name} | ${originalLoad} → ${statsResult.weight} lbs (${statsResult.method}, ${(statsResult.confidence * 100).toFixed(0)}% conf)`);
        }
      } catch (err) {
        console.log(` P1 Statistical failed for ${exercise.name}:`, err);
      }

      // MERGE P0 + P1: Use highest confidence, boost when they agree
      // Per 2025 meta-analysis: multi-session data (P1) > single-session RPE (P0)
      if (p0Result && p1Result) {
        // Map P0 confidence string to numeric: high=0.85, medium=0.6, low=0.35
        const p0Confidence = p0Result.confidence === 'high' ? 0.85 : p0Result.confidence === 'medium' ? 0.6 : 0.35;
        const p1Confidence = p1Result.confidence;
        const p0Direction = p0Result.weight > originalLoad ? 'increase' : p0Result.weight < originalLoad ? 'decrease' : 'maintain';
        const p1Direction = p1Result.weight > originalLoad ? 'increase' : p1Result.weight < originalLoad ? 'decrease' : 'maintain';

        if (p0Direction === p1Direction) {
          // Both agree — use P1 (more data) but boost confidence
          finalLoad = p1Result.weight;
          adjustmentReason = `${p1Result.reasoning} (confirmed by session analysis)`;
          adjustmentSource = 'statistics';
          statisticalData = { method: p1Result.method as 'trend' | 'rpe-adjusted' | 'recent-average' | 'program-weight', confidence: Math.min(0.95, p1Confidence + 0.1), dataPoints: p1Result.dataPoints || 0, trend: p1Result.trend };
          console.log(` Merged (agree): ${exercise.name} → ${finalLoad} lbs (both say ${p0Direction})`);
        } else {
          // They disagree — use the one with higher confidence
          if (p1Confidence >= p0Confidence) {
            finalLoad = p1Result.weight;
            adjustmentReason = p1Result.reasoning;
            adjustmentSource = 'statistics';
            statisticalData = { method: p1Result.method as 'trend' | 'rpe-adjusted' | 'recent-average' | 'program-weight', confidence: p1Confidence, dataPoints: p1Result.dataPoints || 0, trend: p1Result.trend };
            console.log(` Merged (P1 wins): ${exercise.name} → ${finalLoad} lbs (P1 conf ${(p1Confidence * 100).toFixed(0)}% > P0 ${(p0Confidence * 100).toFixed(0)}%)`);
          } else {
            finalLoad = p0Result.weight;
            adjustmentReason = p0Result.reason || 'Session-based progression';
            adjustmentSource = 'statistics';
            console.log(` Merged (P0 wins): ${exercise.name} → ${finalLoad} lbs (P0 conf ${(p0Confidence * 100).toFixed(0)}% > P1 ${(p1Confidence * 100).toFixed(0)}%)`);
          }
        }
        if (finalLoad !== originalLoad) {
          adjustmentSources.push({
            type: 'statistics',
            multiplier: originalLoad > 0 ? finalLoad / originalLoad : 1,
            reason: adjustmentReason,
          });
        }
      } else if (p1Result) {
        // Only P1 has data
        finalLoad = p1Result.weight;
        adjustmentReason = p1Result.reasoning;
        adjustmentSource = 'statistics';
        statisticalData = { method: p1Result.method as 'trend' | 'rpe-adjusted' | 'recent-average' | 'program-weight', confidence: p1Result.confidence, dataPoints: p1Result.dataPoints || 0, trend: p1Result.trend };
        if (finalLoad !== originalLoad) {
          adjustmentSources.push({
            type: 'statistics',
            multiplier: originalLoad > 0 ? finalLoad / originalLoad : 1,
            reason: `${p1Result.method} (${(p1Result.confidence * 100).toFixed(0)}% conf): ${p1Result.reasoning}`,
          });
        }
      } else if (p0Result) {
        // Only P0 has data
        finalLoad = p0Result.weight;
        adjustmentReason = p0Result.reason || 'Session-based progression';
        adjustmentSource = 'statistics';
        if (finalLoad !== originalLoad) {
          adjustmentSources.push({
            type: 'statistics',
            multiplier: originalLoad > 0 ? finalLoad / originalLoad : 1,
            reason: adjustmentReason,
          });
        }
      }

      // P2: HYBRID RULES (fallback when neither P0 nor P1 produced results)
      if (adjustmentSource === 'rule') {
        try {
          // readinessMultiplier = 1.0 — same double-count fix as P1 above.
          const hybridRec = await getHybridRecommendation(
            exercise.name, targetReps, originalLoad, 1.0
          );
          if (hybridRec.suggestedWeight !== originalLoad) {
            finalLoad = hybridRec.suggestedWeight;
            adjustmentReason = hybridRec.reasoning;
            adjustmentSource = 'rule';
          }
        } catch (error) {
          console.log(` Hybrid recommendation failed for ${exercise.name}:`, error);
        }
      }

      // PHASE-AWARE PROGRESSION CAP
      // Accumulation = conservative (high volume), Intensification = aggressive (low volume)
      if (periodizationPhase && finalLoad > originalLoad && originalLoad > 0) {
        const increasePercent = ((finalLoad - originalLoad) / originalLoad) * 100;
        let maxIncreasePercent = 5.0; // Default
        const phaseType = periodizationPhase.type || '';
        if (phaseType === 'accumulation' || phaseType === 'hypertrophy') {
          maxIncreasePercent = 2.5;
        } else if (phaseType === 'intensification' || phaseType === 'strength') {
          maxIncreasePercent = 5.0;
        } else if (phaseType === 'deload') {
          maxIncreasePercent = 0;
        }
        if (increasePercent > maxIncreasePercent && maxIncreasePercent > 0) {
          // ==================================================================
          // DEMONSTRATED-CAPACITY BYPASS (audit P1-9 — 2026-07-14)
          //
          // The cap exists to throttle SPECULATIVE progression — pushing the
          // athlete past anything they've shown. But the engines' number is
          // often a MEASUREMENT CORRECTION: the user already lifted far above
          // the stale baseline (e.g. rec 405 leg press, did 530 at RPE ≤8),
          // and clamping that read to +2.5–5% of the stale number is why the
          // system took ~8 sessions to catch up (the reported failure; pinned
          // by loadPath.e2e.sim "PHASE CAP does not silently discard...").
          //
          // Rule: the ceiling is the GREATER of the phase cap and the max
          // load the athlete has demonstrated at the target rep scheme in
          // their recent completed sets. Anything the engines propose UP TO
          // demonstrated capacity is a correction and passes; anything above
          // it is speculation and still gets capped. Evidence is strictly
          // demonstration-based:
          //  - a completed set at >= target reps proves that WEIGHT at target
          //    reps outright;
          //  - a completed set at FEWER reps (>=3, <=12 — the Helms/RTS chart
          //    + e1RM validity range) is DOWN-converted to its target-rep
          //    equivalent via the reps×RPE %1RM chart, i.e. we only ever
          //    interpolate toward an easier prescription, never extrapolate
          //    a heavier load than was actually lifted.
          // ==================================================================
          const cappedLoad = roundToNearestPlate(Math.round(originalLoad * (1 + maxIncreasePercent / 100)));
          let demonstrated = 0;
          try {
            const [{ getExerciseHistory }, { percentForRepsRPE }] = await Promise.all([
              import('./statisticalRecommendation'),
              import('./oneRM'),
            ]);
            const hist = await getExerciseHistory(exercise.name);
            if (hist && hist.length > 0) {
              // Same recency window as the demonstrated-capacity floor: the
              // last ~8 logged sets. No ancient one-off PRs.
              for (const h of hist.slice(-8)) {
                if (!h.wasSuccessful || h.weight <= 0 || h.reps < 3 || h.reps > 12) continue;
                const rpe = h.rpe ?? 7.5; // ratio is insensitive to the default
                const equivalent = h.reps >= targetReps
                  ? h.weight // did the weight for target reps (or more) — proven outright
                  : h.weight * (percentForRepsRPE(targetReps, rpe) / percentForRepsRPE(h.reps, rpe));
                if (equivalent > demonstrated) demonstrated = equivalent;
              }
            }
          } catch { /* non-critical — fall back to the plain cap */ }

          const ceiling = Math.max(cappedLoad, roundToNearestPlate(Math.round(demonstrated)));
          if (finalLoad > ceiling) {
            if (ceiling > cappedLoad) {
              reasoning.push(`Phase cap (${phaseType}): engine's ${increasePercent.toFixed(1)}% increase capped at demonstrated capacity (${ceiling} lb) — measurement corrections pass, speculation doesn't`);
            } else {
              reasoning.push(`Phase cap (${phaseType}): ${increasePercent.toFixed(1)}% increase capped to ${maxIncreasePercent}%`);
            }
            finalLoad = ceiling;
          } else if (finalLoad > cappedLoad) {
            reasoning.push(`Phase cap bypassed: ${finalLoad} lb is within recently demonstrated capacity (re-anchor, not progression)`);
          }
        }
      }

      // POST-DELOAD SUPERCOMPENSATION BOOST (+3%)
      // Delphi consensus: post-deload is opportunity to increase training intensity.
      //
      // MUTUALLY EXCLUSIVE with the high-recovery +3% that rides in
      // intensityAdjustment (see the readiness block above, `recoveryScore >= 85`).
      // They are the same claim — "you are fresh, take more" — counted twice,
      // and both landed AFTER the phase cap, so a well-rested lifter coming off
      // a deload got 1.03 x 1.03 = +6.1% on top of a ceiling that was supposed
      // to bind. The bigger of the two applies, once, and the result is still
      // held to the phase cap computed above.
      const readinessAlreadyBoosting = intensityAdjustment > 1.0;
      if (postDeloadBoost && finalLoad > 0 && !readinessAlreadyBoosting) {
        const preBoost = finalLoad;
        finalLoad = roundToNearestPlate(Math.round(finalLoad * 1.03));
        if (finalLoad !== preBoost) {
          adjustmentSources.push({
            type: 'rule',
            multiplier: 1.03,
            reason: 'Post-deload supercompensation: +3% intensity',
          });
          console.log(` Post-deload boost: ${exercise.name} | ${preBoost} → ${finalLoad} lbs (+3%)`);
        }
      }

      // ====================================================================
      // LAYER 2: WITHIN-SESSION MOVEMENT-PATTERN FATIGUE — REMOVED 2026-07-14.
      //
      // This trimmed the load by 3% per "overlapping set" from any earlier
      // exercise sharing a movement pattern (floor 0.85). Two defects, one fatal:
      //
      //  1. FATAL: `exerciseOrder` is built from the FULL enriched workout, which
      //     includes the prep/warmup block. Warmup movements match the pattern
      //     lists — a "Bodyweight Walking Lunge" in the warmup is 'knee-dominant'
      //     — so the enricher's own warmup docked 9% off the first working squat.
      //     Every session. Silently. (Caught by the e2e load-path test: a normal
      //     day prescribed 219 lb instead of 240.)
      //  2. Conceptually redundant even when it worked: the program template
      //     ALREADY chose the exercise order, and the prescription for a lift
      //     placed second is what it is BECAUSE it comes second. Trimming again
      //     re-penalizes the template's own design.
      //
      // Genuine within-session fatigue is handled where the evidence actually is:
      // the intra-session RPE gate (reduces load on the remaining sets when RPE
      // says the athlete is grinding) and the next-session progression engines.
      // ====================================================================

      // PLATEAU DETECTION: Structured reload cycle (reduce → rebuild → attempt)
      // Instead of just reducing, program a 3-session cycle to break through
      //
      // Carries are excluded. detectPlateau's "no progress" test compares
      // maxWeight and maxReps — and on a carry `reps` is metres, so a lifter
      // holding 75 lb and walking a steady 40 m reads as a stall on both axes
      // by construction. The observed result was an escalating ladder on a
      // perfectly healthy carry (3 sessions → -5%, 4 → -5%, 5 → -10% "rebuild
      // phase"), every rung of which was then silently cancelled by the
      // demonstrated-capacity floor below — so the athlete was told they were
      // plateaued and being deloaded while the prescription never actually
      // moved off 75 lb. Cutting load is the wrong lever regardless: on a carry
      // the load is the fixed term and distance is the progression variable.
      if (!isCircuitExercise && !isNonStrength && !isCarryExercise) {
        try {
          const plateau = await detectPlateau(exercise.name);
          if (plateau.isPlateaued) {
            plateauData = plateau;
            const sessions = plateau.sessionsSinceProgress || 0;
            console.log(` Plateau detected: ${exercise.name} | ${sessions} sessions stalled`);

            if (originalLoad > 0) {
              if (sessions >= 5) {
                // Long plateau: reduce 10% to break the pattern
                const prevLoad = finalLoad;
                plateauTrimFromLoad = prevLoad;
                finalLoad = Math.round(finalLoad * 0.90);
                adjustmentSources.push({
                  type: 'rule',
                  multiplier: 0.90,
                  reason: `Plateau reload: ${sessions} sessions stalled → -10% (rebuild phase)`,
                });
                reasoning.push(`[PLATEAU] ${exercise.name}: ${sessions} sessions stalled — starting reload cycle (-10%)`);
                console.log(` Plateau reload: ${exercise.name} | ${prevLoad} → ${finalLoad} lbs (-10%)`);
              } else if (sessions >= 3) {
                // Early plateau: small nudge down
                const prevLoad = finalLoad;
                plateauTrimFromLoad = prevLoad;
                finalLoad = Math.round(finalLoad * 0.95);
                adjustmentSources.push({
                  type: 'rule',
                  multiplier: 0.95,
                  reason: `Plateau nudge: ${sessions} sessions stalled → -5%`,
                });
                reasoning.push(`[PLATEAU] ${exercise.name}: ${plateau.suggestion}`);
                console.log(` Plateau nudge: ${exercise.name} | ${prevLoad} → ${finalLoad} lbs (-5%)`);
              }
            }
          }
        } catch { /* non-critical */ }
      }

      // SYNTHESIS FLAGS (exercise-specific signals from metrics analysis)
      if (!isCircuitExercise && !isNonStrength) {
        const flags = exerciseFlagMap.get(exercise.name.toLowerCase());
        if (flags) {
          for (const f of flags) {
            if (f.flag === 'sfr-swap') {
              const prevLoad = finalLoad;
              finalLoad = Math.round(finalLoad * 0.95);
              adjustmentSources.push({
                type: 'rule',
                multiplier: 0.95,
                reason: `SFR flag: ${f.detail} → -5% load`,
              });
              console.log(` SFR flag: ${exercise.name} | ${prevLoad} → ${finalLoad} lbs (-5%)`);
            }
            if (f.flag === 'plateau-swap') {
              reasoning.push(`[SWAP SUGGESTED] ${exercise.name}: ${f.detail}`);
              console.log(` Plateau-swap flag: ${exercise.name} | ${f.detail}`);
            }
          }
        }
      }
    } // end of non-deload branch

    // SAFETY FLOOR: prevent cascading multipliers from going below 60% of original
    if (originalLoad > 0 && finalLoad < originalLoad * 0.6) {
      const floored = Math.round(originalLoad * 0.6);
      console.log(` Safety floor: ${exercise.name} | ${finalLoad} → ${floored} lbs (clamped to 60% of program weight ${originalLoad})`);
      finalLoad = floored;
    }

    // REENTRY CEILING (applied AFTER the safety floor so it can sit below 60%).
    // The full smart chain ran for reentry lifts (brain on); here we bound the
    // ENGINE's output so a returning lifter can autoregulate DOWN if they're
    // grinding, but can never progress ABOVE their pre-break baseline on stale
    // data.
    //
    // The ramp factor itself is NOT multiplied in here any more — it lives in
    // intensityAdjustment and is applied once by the session multiplier below.
    // Clamping the pre-multiplier value to [originalLoad×0.8, originalLoad] and
    // then multiplying by reentryFactor yields exactly the intended band
    // [pre-break × 0.8 × factor, pre-break × factor]. Previously the ceiling
    // multiplied by reentryFactor while the template load had already been
    // scaled by it, so %-based lifts came out at factor² of pre-break.
    if (reentryTriggered && originalLoad > 0) {
      const bandFloor = Math.round(originalLoad * 0.8);
      const capped = Math.max(bandFloor, Math.min(originalLoad, finalLoad));
      if (capped !== finalLoad) {
        console.log(`↩ Reentry ceiling: ${exercise.name} | ${finalLoad} → ${capped} lbs (bounded to baseline before the ${Math.round(reentryFactor * 100)}% ramp cap)`);
        finalLoad = capped;
      }
      adjustmentSources.push({
        type: 'rule',
        multiplier: reentryFactor,
        reason: reentryReasonShort
          ? `${reentryReasonShort} — autoregulated within the ramp band.`
          : `Return-to-training: ${reentryDays} days off, autoregulated within the ${Math.round(reentryFactor * 100)}% ramp band.`,
      });
    }

    // ========================================================================
    // DEMONSTRATED-CAPACITY FLOOR
    //
    // Never prescribe BELOW a weight the lifter has recently PROVEN they can
    // do for the target reps. The stacked caps above (phase cap 2.5–5%, RPE
    // dampers, plateau trims) can otherwise clamp the prescription far under
    // actual performance — the reported failure mode was "recommended 30 lb
    // after I logged 90 lb for the same reps." Flooring to recent proven
    // performance fixes that without ever inflating past what was lifted.
    //
    // Conservative by construction:
    //  - only COMPLETED sets count (wasSuccessful),
    //  - the proven set must have been done at the target rep range or higher
    //    (so a heavy low-rep single can't peg a high-rep accessory),
    //  - only the last ~8 logged sets are considered (no ancient one-off PR),
    //  - skipped on deload / reentry / non-strength (those intentionally pull
    //    the weight down).
    // ========================================================================
    // Skip assisted movements — their "weight" is assistance (higher = easier),
    // so flooring UP to the most recently completed value would push toward
    // MORE help, i.e. make it easier. The floor's whole premise (bigger = more
    // proven capacity) is inverted for assisted work.
    if (!isDeloadWeek && !reentryTriggered && !isNonStrength && !isCircuitExercise && !isAssistedExercise(exercise.name)) {
      try {
        const { getExerciseHistory } = await import('./statisticalRecommendation');
        const hist = await getExerciseHistory(exercise.name);
        if (hist && hist.length > 0) {
          const proven = hist
            .slice(-8)
            .filter(h => h.wasSuccessful && h.weight > 0 && h.reps >= targetReps - 1)
            .reduce((mx, h) => Math.max(mx, h.weight), 0);

          /**
           * THE FLOOR MUST NOT OVERRULE A BAD DAY.
           *
           * This floor is RPE-blind: it asks only "did they ever complete this
           * load", never "how did the last session actually go". Because it
           * runs downstream of every de-escalation (P0 decrease, P1 decrease,
           * plateau trim, SFR trim), it silently erased them — an audit of 73
           * prescriptions found 40 restored to the proven weight, and the
           * cancelled reason was still printed, so the athlete read
           * "RPE 9 → -3% adjustment → Held at 185 lb" in one sentence.
           *
           * Proving you can lift 185 last month does not mean you should be
           * given 185 the day after grinding it at RPE 9 or missing reps. The
           * floor's real job is stopping an absurd collapse (the reported "30
           * lb after I logged 90"), and it still does that — it is only stood
           * down when the MOST RECENT session provides direct evidence against
           * it. Same rule as the statistical engine's grind gate.
           */
          const lastDay = hist[hist.length - 1].date.toISOString().slice(0, 10);
          const lastSession = hist.filter(h => h.date.toISOString().slice(0, 10) === lastDay);
          const lastSessionStruggled = lastSession.some(
            h => (h.rpe ?? 0) >= 9 || h.wasSuccessful === false,
          );
          if (lastSessionStruggled && finalLoad < proven) {
            console.log(` Capacity floor STOOD DOWN for ${exercise.name}: last session graded RPE>=9 or missed — honouring the de-escalation to ${finalLoad} lb`);
          }

          if (!lastSessionStruggled && proven > 0 && finalLoad < proven) {
            console.log(` Capacity floor: ${exercise.name} | ${finalLoad} → ${proven} lbs (recently completed ${proven} for ${targetReps}+ ${repUnitLabel})`);
            adjustmentSources.push({
              type: 'rule',
              multiplier: originalLoad > 0 ? proven / originalLoad : 1,
              reason: `Held at ${proven} lb — you recently completed that for ${targetReps}+ ${repUnitLabel}.`,
            });
            finalLoad = proven;

            // If the floor lifted the weight back to (or past) where it stood
            // before a plateau trim, that trim had no effect on the
            // prescription. Retract it rather than leaving the athlete reading
            // a deload notice next to an unchanged weight.
            //
            // Retract EVERY reduction the floor just cancelled, not only
            // plateau trims. The original splice matched `startsWith('Plateau ')`
            // alone, so a P0/P1 decrease or an SFR trim that the floor
            // overrode stayed in the explanation and the athlete read a
            // reduction next to an unchanged — or higher — weight. Any source
            // claiming to lower the load, when the load did not end up lower,
            // is a false statement about what the engine did.
            if (finalLoad >= originalLoad) {
              const cancelled: string[] = [];
              for (let i = adjustmentSources.length - 1; i >= 0; i--) {
                const src = adjustmentSources[i];
                // The floor's own entry must survive; it is the true reason.
                if (src.reason.startsWith('Held at ')) continue;
                if (src.multiplier < 1) {
                  cancelled.push(src.reason);
                  adjustmentSources.splice(i, 1);
                }
              }
              for (let i = reasoning.length - 1; i >= 0; i--) {
                if (reasoning[i].startsWith('[PLATEAU]')) reasoning.splice(i, 1);
              }
              if (plateauTrimFromLoad !== null) {
                plateauData = undefined;
                plateauTrimFromLoad = null;
              }
              if (cancelled.length > 0) {
                console.log(` Capacity floor superseded ${cancelled.length} reduction(s) for ${exercise.name} — retracted from the explanation:`, cancelled);
              }
            }
          }
        }
      } catch { /* non-critical */ }
    }

    // ========================================================================
    // SINGLE SESSION INTENSITY MULTIPLIER  (audit P0-1/2/3 — 2026-07-14)
    //
    // Everything above produced a FULL-INTENSITY prescription: the baseline
    // (template %1RM / e1RM anchor / last-used / first-time), the progression
    // engines, the caps and the floors all operate on un-modulated loads.
    //
    // Here — and ONLY here — we apply today's session-level scalar. It is the
    // single binding cap chosen by the decision table (reentry > deload >
    // damper chain > recovery debt > … ) with the synthesis fine-tune folded in,
    // already clamped by the safety floors upstream. One signal, one
    // multiplication, one line in the coach panel.
    //
    // This replaces three inconsistent application points (template scaling in
    // generateWorkout, the per-exercise deload ×0.7, the statistical engine's
    // readinessMultiplier, and the reentry ceiling's ×factor) which between them
    // squared the same scalar and produced 49% deloads and 0.81× "10%" readiness
    // trims. It also subsumes the old LOADED-CONDITIONING READINESS TRIM: sleds,
    // drags and carries route through this path like every other loaded lift, so
    // they now track recovery without needing a bespoke patch.
    //
    // Applied AFTER the demonstrated-capacity floor deliberately: on a normal day
    // the multiplier is 1.0 and the two never interact, but when a genuine cap
    // HAS fired (deload / reentry / low recovery) the trim is allowed to take the
    // athlete below their proven capacity — that is the entire point of the trim.
    //
    // ASSISTED lifts are EXCLUDED: their "load" is ASSISTANCE (bigger number =
    // easier), so a deload/readiness trim (×0.70/×0.90) would CUT assistance and
    // make the lift HARDER on the day the athlete is supposed to go easier —
    // the same inversion the RPE gate, the statistical engine and the
    // demonstrated-capacity floor already correct for. The honest inversion
    // (scale the effective load BW − assistance) needs a bodyweight we don't
    // reliably have here, so we hold assistance at baseline instead: on a
    // trim day an unchanged assisted lift is at worst neutral, never harder.
    // ========================================================================
    if (originalLoad > 0 && finalLoad > 0 && Math.abs(intensityAdjustment - 1.0) > 0.001 &&
        !isAssistedExercise(exercise.name)) {
      const preMultiplier = finalLoad;
      finalLoad = roundToNearestPlate(Math.round(finalLoad * intensityAdjustment));
      if (finalLoad !== preMultiplier) {
        const pct = Math.round(intensityAdjustment * 100);
        adjustmentSources.push({
          type: intensityAdjustment < 1 ? 'recovery' : 'rule',
          multiplier: intensityAdjustment,
          reason: deloadTriggered
            ? `Deload: training at ${pct}% intensity.`
            : reentryTriggered
              ? `Return-to-training ramp: capped at ${pct}% of pre-break load.`
              : intensityAdjustment < 1
                ? `Readiness ${pct}% — load trimmed for recovery.`
                : `Recovered and ready — load raised to ${pct}%.`,
        });
        console.log(`[session-intensity] ${exercise.name} | ${preMultiplier} → ${finalLoad} lbs (×${intensityAdjustment.toFixed(3)})`);
      }
    }

    finalLoad = roundToNearestPlate(finalLoad);

    // ========================================================================
    // P4: VOLUME AUTOREGULATION (sets) — RPE-based nudge only.
    //
    // The session volume scalar (volumeAdjustment) is applied ONCE, upstream in
    // generateWorkout, which is the only layer that can also scale cardio and
    // duration-based set counts. This step therefore no longer re-applies it —
    // it used to (`Math.round(baseSets * volumeAdjustment)`) on top of the
    // already-reduced count coming back from generateWorkout, so a deload cut
    // volume by 0.5 × 0.5 = 0.25 and a 4-set exercise collapsed to 1 set instead
    // of 2 (audit P0-2, 2026-07-14).
    //
    // What remains here is the per-exercise RPE nudge, which generateWorkout
    // cannot do because it has no history:
    //   • last session's RPE ≥9 (grind) → trim one set
    //   • last RPE ≤6 AND well-recovered AND not deload/reentry → earn one set
    // Clamped to [base−2, base+1] and a hard [1,6] so nothing runs away.
    // ========================================================================
    // Conditioning (carries, sleds, drags) is prescribed in DISTANCE per round,
    // not barbell sets — trimming a "set" via strength-RPE logic is wrong for it.
    // Exempt it from volume autoregulation (it has its own pace/round logic).
    const isConditioning = /carry|farmer|suitcase|sled|prowler/i.test(exercise.name) ||
      (/\bdrag\b/i.test(exercise.name) && !/curl/i.test(exercise.name));
    const setsMatch = exercise.sets_reps.match(/^(\d+)\s*[x×]/i);
    if (setsMatch && originalLoad > 0 && !isConditioning) {
      const baseSets = parseInt(setsMatch[1]);
      let targetSets = baseSets;

      let volNote = '';
      try {
        const { getExerciseHistory } = await import('./statisticalRecommendation');
        const hist = await getExerciseHistory(exercise.name);
        const lastRpe = hist.length > 0 ? hist[hist.length - 1].rpe : null;
        const wellRecovered = recoveryScore >= recoveryOptimal;
        if (lastRpe !== null && lastRpe >= 9) {
          targetSets -= 1;
          volNote = `last RPE ${lastRpe} — trim a set`;
        } else if (lastRpe !== null && lastRpe <= 6 && wellRecovered &&
                   !deloadTriggered && !reentryTriggered) {
          targetSets += 1;
          volNote = `last RPE ${lastRpe} + recovered — earn a set`;
        }
      } catch { /* non-critical — keep base set count */ }

      // Clamp: never more than +1 over base, never below base−2, hard [1,6].
      targetSets = Math.max(1, Math.min(6, Math.max(baseSets - 2, Math.min(baseSets + 1, targetSets))));

      if (targetSets !== baseSets) {
        exercise.sets_reps = exercise.sets_reps.replace(/^\d+(?=\s*[x×])/i, String(targetSets));
        adjustmentSources.push({
          type: 'recovery',
          multiplier: 1.0,
          reason: `Volume: ${baseSets} to ${targetSets} sets${volNote ? ` (${volNote})` : ''}`,
        });
        console.log(` P4 volume: ${exercise.name} | ${baseSets} → ${targetSets} sets${volNote ? ` (${volNote})` : ''}`);
      }
    }

    // ========================================================================
    // CARRY DISTANCE PROGRESSION
    //
    // Runs after every load cap, floor and the session intensity multiplier,
    // because distance is the carry's primary progression lever and it must be
    // decided against the load actually being prescribed today. It sits just
    // ahead of the adjustment records so they capture the final prescription.
    //
    // Before this, nothing in the engine ever moved a carry's metres: the whole
    // progression stack reasons about weight, so an athlete walking farther
    // under the same bar made no impression on it at all. See lib/carryProgression.ts.
    //
    // The load bump (when distance tops out the range) is suppressed whenever a
    // session-level trim is in force — on a deload or low-recovery day the
    // decision table has already said "less today", and a carry is not the
    // place to overrule it.
    // ========================================================================
    if (isCarryExercise && !isDeloadWeek && !deloadTriggered && !reentryTriggered) {
      try {
        const spec = parseCarryDistance(exercise.sets_reps);
        if (spec) {
          const { getExerciseHistory } = await import('./statisticalRecommendation');
          const hist = await getExerciseHistory(exercise.name);
          const carryHistory: CarryHistoryPoint[] = (hist || []).map(h => ({
            date: h.date,
            weight: h.weight,
            distance: h.reps, // the log stores METRES in `reps` on a carry
            rpe: h.rpe,
            wasSuccessful: h.wasSuccessful,
          }));

          const prog = getCarryProgression(spec, finalLoad, carryHistory);
          if (prog.action !== 'insufficient-data') {
            const allowLoadBump = intensityAdjustment >= 1.0;
            const nextWeight = prog.action === 'add-load' && allowLoadBump ? prog.weight : finalLoad;
            const nextSetsReps = applyCarryDistance(exercise.sets_reps, prog.distance);

            if (nextSetsReps !== exercise.sets_reps || nextWeight !== finalLoad) {
              adjustmentSources.push({
                type: 'rule',
                multiplier: finalLoad > 0 ? nextWeight / finalLoad : 1,
                reason: prog.action === 'add-load' && !allowLoadBump
                  ? `${prog.reason.replace(/ Load goes to .*$/, '')} Load holds today — the session is already trimmed.`
                  : prog.reason,
              });
              console.log(`[carry] ${exercise.name}: ${exercise.sets_reps} @${finalLoad} → ${nextSetsReps} @${nextWeight} (${prog.action})`);
              finalLoad = nextWeight;
              // Mutating in place (rather than building a copy) keeps this
              // consistent with the P4 volume step above, and ensures the
              // adjustment records built below see the final prescription.
              exercise.sets_reps = nextSetsReps;
              exercise.targetDistance = prog.distance;
            }
          }
        }
      } catch (err) {
        console.log(`[carry] progression failed for ${exercise.name} (non-critical):`, err);
      }
    }

    // ========================================================================
    // TIMED HOLD DURATION PROGRESSION
    //
    // Same placement and rationale as the carry block above: duration is the
    // progression lever, and it must be decided against the load actually
    // being prescribed today. Load bumps are suppressed when a session-level
    // trim is already in force.
    // ========================================================================
    if (isTimedHold && !isCarryExercise && !isDeloadWeek && !deloadTriggered && !reentryTriggered) {
      try {
        const spec = parseHoldDuration(exercise.sets_reps);
        if (spec) {
          const { getExerciseHistory } = await import('./statisticalRecommendation');
          const hist = await getExerciseHistory(exercise.name);
          // On a timed log the seconds live in the `reps` slot, the same
          // convention carries use for metres.
          // Prefer `durationSec` — what was ACTUALLY held — over the `reps`
          // slot, which on a timed set carries the seeded TARGET and only
          // matches reality when the athlete left the field untouched.
          // Progressing off your own prescription is a feedback loop that
          // confirms whatever the engine already believed.
          const holdHistory: TimedHoldHistoryPoint[] = (hist || [])
            .map(h => ({
              date: h.date,
              seconds: h.durationSec ?? h.reps,
              weight: h.weight,
              rpe: h.rpe,
              wasSuccessful: h.wasSuccessful,
            }))
            .filter(h => h.seconds > 0);

          // Only movements that sensibly take external load get the load bump.
          const canLoad = !/dead ?hang|wall sit|hollow/i.test(lowerName);
          const prog = getTimedHoldProgression(spec, finalLoad, holdHistory, canLoad);
          if (prog.action !== 'insufficient-data') {
            const allowLoadBump = intensityAdjustment >= 1.0;
            const nextWeight = prog.action === 'add-load' && allowLoadBump ? prog.weight : finalLoad;
            const nextSetsReps = applyHoldDuration(exercise.sets_reps, prog.seconds);

            if (nextSetsReps !== exercise.sets_reps || nextWeight !== finalLoad) {
              adjustmentSources.push({
                type: 'rule',
                multiplier: finalLoad > 0 ? nextWeight / finalLoad : 1,
                reason: prog.action === 'add-load' && !allowLoadBump
                  ? `${prog.reason.replace(/ Add \d+ lb.*$/, '')} Load holds today — the session is already trimmed.`
                  : prog.reason,
              });
              console.log(`[timed] ${exercise.name}: ${exercise.sets_reps} → ${nextSetsReps} @${nextWeight} (${prog.action})`);
              finalLoad = nextWeight;
              exercise.sets_reps = nextSetsReps;
            }
          }
        }
      } catch (err) {
        console.log(`[timed] progression failed for ${exercise.name} (non-critical):`, err);
      }
    }

    // Build adjustment record for SmartWorkoutResult.adjustments array
    let adjustment: ExerciseAdjustment | null = null;
    if (finalLoad !== originalLoad || plateauData?.isPlateaued || patternFatigueData) {
      adjustment = {
        exerciseName: exercise.name,
        originalLoad,
        adjustedLoad: finalLoad,
        adjustmentPercent: originalLoad > 0 ? ((finalLoad - originalLoad) / originalLoad) * 100 : 0,
        reason: adjustmentReason,
        source: adjustmentSource,
        statistical: statisticalData,
        plateau: plateauData,
        patternFatigue: patternFatigueData,
      };
    }

    // Build the AppliedAdjustment object for the exercise itself
    // This shows the full adjustment chain inline with the exercise
    const totalMultiplier = adjustmentSources.reduce((acc, src) => acc * src.multiplier, 1);
    const appliedAdjustment: AppliedAdjustment | undefined = adjustmentSources.length > 0 || finalLoad !== originalLoad
      ? {
          applied: finalLoad !== originalLoad,
          baselineLoad: originalLoad,
          sources: adjustmentSources.length > 0 ? adjustmentSources : [{
            type: 'periodization',
            multiplier: 1,
            reason: 'Program baseline',
          }],
          totalMultiplier: totalMultiplier !== 1 ? totalMultiplier : (originalLoad > 0 ? finalLoad / originalLoad : 1),
          explanation: adjustmentSources.length > 0
            ? adjustmentSources.map(s => s.reason).join(' → ')
            : (finalLoad !== originalLoad ? adjustmentReason : 'No adjustment needed'),
        }
      : undefined;

    return {
      exercise: {
        ...exercise,
        target_load: finalLoad,
        // Add the adjustment chain directly to the exercise for UI display
        adjustment: appliedAdjustment,
      },
      adjustment,
    };
  }));

  // Collect results - preserves original order from Promise.all
  const enhancedWorkout = enhancedResults.map(r => r.exercise);
  for (const result of enhancedResults) {
    if (result.adjustment) {
      adjustments.push(result.adjustment);
    }
  }
  
  // ============================================================================
  // STEP 7: CHECK FOR EXERCISE SUBSTITUTIONS (Injury Prevention)
  // ============================================================================
  
  let substitutions: SubstitutionResult[] = [];
  
  // Only check substitutions if not in deload (deload already reduces risk)
  if (!deloadTriggered && enhancedWorkout.length > 0) {
    try {
      substitutions = await getWorkoutSubstitutions(
        enhancedWorkout.map(e => ({ name: e.name, targetLoad: e.target_load }))
      );
      
      if (substitutions.length > 0) {
        reasoning.push(`${substitutions.length} exercise substitutions suggested for injury prevention`);
        console.log(` Smart Workout: ${substitutions.length} substitutions available`);
      }
    } catch (error) {
      console.log(' Substitution check failed:', error);
    }
  }
  
  // ============================================================================
  // STEP 7b: INJURY BREAK CONSTRAINTS
  // ============================================================================

  try {
    const { getActiveBreak, shouldAvoidExercise } = await import('./trainingBreak');
    const activeBreak = userId ? await getActiveBreak(userId) : null;

    if (activeBreak?.injury) {
      const injury = activeBreak.injury;
      const rtt = injury.returnToTraining;

      // Apply intensity/volume multipliers from return-to-training phase
      if (rtt && rtt.intensityPct < 100) {
        const intensityMult = rtt.intensityPct / 100;
        const volumeMult = rtt.volumePct / 100;

        for (const ex of enhancedWorkout) {
          // Apply intensity multiplier to loads
          if (ex.target_load) {
            const preInjuryLoad = ex.target_load;
            ex.target_load = Math.round(ex.target_load * intensityMult / 2.5) * 2.5;

            // CRITICAL: keep the adjustments banner in sync with the workout
            // card. The per-exercise loop already produced an adjustment
            // record for this exercise with adjustedLoad = preInjuryLoad. If
            // we just push a SECOND record here, the banner shows the old
            // value while the workout card shows the new one (user-reported
            // bug). Mutate the existing record instead.
            const existing = adjustments.find(a => a.exerciseName === ex.name);
            if (existing) {
              // Recovery phase chained on top of the prior adjustment —
              // overwrite the adjustedLoad + percent so the banner reflects
              // the FINAL target, and append the reason so the user can see
              // what stacked.
              existing.adjustedLoad = ex.target_load;
              existing.adjustmentPercent = existing.originalLoad > 0
                ? ((ex.target_load - existing.originalLoad) / existing.originalLoad) * 100
                : 0;
              existing.reason = `${existing.reason} → Recovery phase: ${rtt.phase} (${rtt.intensityPct}%)`;
            } else {
              // No prior adjustment — push a fresh recovery-only entry.
              adjustments.push({
                exerciseName: ex.name,
                originalLoad: preInjuryLoad,
                adjustedLoad: ex.target_load,
                adjustmentPercent: (intensityMult - 1) * 100,
                reason: `Recovery phase: ${rtt.phase} (${rtt.intensityPct}% intensity)`,
                source: 'recovery',
              });
            }
          }

          // Apply volume multiplier to sets (reduce set count in sets_reps string)
          if (volumeMult < 1 && ex.sets_reps) {
            const setsMatch = ex.sets_reps.match(/^(\d+)\s*[x×]/i);
            if (setsMatch) {
              const originalSets = parseInt(setsMatch[1]);
              const targetSets = Math.max(1, Math.round(originalSets * volumeMult));
              if (targetSets < originalSets) {
                ex.sets_reps = ex.sets_reps.replace(/^\d+(?=\s*[x×])/i, String(targetSets));
              }
            }
          }
        }

        reasoning.push(`Injury recovery: ${rtt.phase} phase — intensity at ${rtt.intensityPct}%, volume at ${rtt.volumePct}%`);
      }

      // Auto-substitute avoided exercises — ACTUALLY swap them into the workout.
      // Previously this only recorded a suggestion and left the contraindicated
      // exercise in place, so the intake screen's "we'll substitute problem
      // exercises automatically" promise was false. We validate the substitute
      // resolves to a real exercise before applying, and keep the adjustments
      // banner keyed to the exercise the card now shows (STEP 7c invariant).
      const { getSubstituteForInjury } = await import('./exerciseSubstitution');
      const { findExercise } = await import('./exerciseDatabase');
      for (let i = 0; i < enhancedWorkout.length; i++) {
        const ex = enhancedWorkout[i];
        if (!shouldAvoidExercise(ex.name, injury)) continue;
        const sub = getSubstituteForInjury(ex.name, injury.location);
        if (!sub) continue;
        // Guard: only swap in a name the rest of the app can actually resolve —
        // never inject a substitute the exercise DB doesn't know.
        const resolved = findExercise(sub.substituteExercise);
        if (!resolved) {
          reasoning.push(`No valid replacement for ${ex.name} (${injury.location}) — flag it manually.`);
          continue;
        }
        const originalName = ex.name;
        ex.name = resolved.name;
        // Re-stamp tracking type WITH the name. Substitutions legitimately cross
        // tracking types (isCompatibleTrackingType permits weight-reps <->
        // bodyweight-reps; INJURY_AVOID_MAP sends Box Jump -> Hip Thrust), and
        // consumers trust ex.trackingType over a live name lookup — so leaving
        // the original's stamp renders a Hip Thrust with no weight cell, or a
        // push-up substitute with a phantom load inherited from the barbell lift.
        ex.trackingType = resolved.trackingType;
        // Crossing into bodyweight drops the inherited load outright. The render
        // and save paths already ignore it once the stamp says bodyweight, but
        // target_load is read directly elsewhere (ML feature extraction), and a
        // 185 lb "load" on a Diamond Push-Up is exactly the phantom-weight class
        // of bad training row this pass exists to stop producing.
        if (resolved.trackingType === 'bodyweight-reps') ex.target_load = 0;
        // Otherwise keep the prior load as a conservative starting point; the swap
        // note tells the user to adjust, and RPE auto-progress corrects it next time.
        ex.details = [ex.details, `Substituted for ${originalName} (${injury.location} injury) — adjust load as needed.`]
          .filter(Boolean).join(' · ');
        const adj = adjustments.find(a => a.exerciseName === originalName);
        if (adj) adj.exerciseName = resolved.name;
        substitutions.push(sub);
        reasoning.push(`Auto-substituted ${originalName} → ${resolved.name} (${injury.location} injury)`);
      }
    }
  } catch (e) {
    // trainingBreak module not available — non-critical
    console.warn('Injury constraint check skipped:', e);
  }

  // ============================================================================
  // STEP 7b-2: CHRONIC INJURY LIMITATIONS (onboarding "injuries to work around")
  // Same avoid + substitute path as an acute injury, but NO intensity/volume cut
  // and no program pause — these are persistent limitations, not a recovery break.
  // ============================================================================
  try {
    const { getChronicInjuries, shouldAvoidExercise } = await import('./trainingBreak');
    const { getSubstituteForInjury } = await import('./exerciseSubstitution');
    const { findExercise } = await import('./exerciseDatabase');
    const chronic = userId ? await getChronicInjuries(userId) : [];
    for (const inj of chronic) {
      for (let i = 0; i < enhancedWorkout.length; i++) {
        const ex = enhancedWorkout[i];
        if (!shouldAvoidExercise(ex.name, inj as any)) continue;
        const sub = getSubstituteForInjury(ex.name, inj.location);
        if (!sub) continue;
        const resolved = findExercise(sub.substituteExercise);
        if (!resolved) continue;
        const originalName = ex.name;
        ex.name = resolved.name;
        // Re-stamp with the name — see the acute-injury site above.
        ex.trackingType = resolved.trackingType;
        if (resolved.trackingType === 'bodyweight-reps') ex.target_load = 0;
        ex.details = [ex.details, `Substituted for ${originalName} (${inj.location} limitation) — adjust load as needed.`]
          .filter(Boolean).join(' · ');
        const adj = adjustments.find(a => a.exerciseName === originalName);
        if (adj) adj.exerciseName = resolved.name;
        substitutions.push(sub);
        reasoning.push(`Working around ${inj.location}: ${originalName} → ${resolved.name}`);
      }
    }
  } catch (e) {
    console.warn('Chronic injury check skipped:', e);
  }

  // ============================================================================
  // STEP 7b-3: INJURY-AWARE PREHAB / POST-HAB INJECTION
  // On top of the exercise substitutions above, inject a short, location-
  // specific prehab set into the warm-up and a matching down-regulation set
  // into recovery whenever an acute injury or chronic limitation is active.
  // Capped at 3 moves each so prep/recovery stays impactful, not long. Prep-
  // like blocks carry target_load 0, so they skip load/ML and the adjustment
  // normalization below.
  // ============================================================================
  try {
    if (userId && enhancedWorkout.length > 0) {
      const { getActiveBreak, getChronicInjuries } = await import('./trainingBreak');
      const { getInjuryPrehab, getInjuryPosthab } = await import('./types/trainingBreak');
      const [brk, chronic] = await Promise.all([
        getActiveBreak(userId),
        getChronicInjuries(userId),
      ]);
      const locations = [
        ...(brk?.injury?.location ? [brk.injury.location] : []),
        ...chronic.map(c => c.location),
      ];
      if (locations.length > 0) {
        const toEx = (
          it: { name: string; sets: number; reps: string; notes: string },
          block: string,
          format: 'prep' | 'recovery',
        ): GeneratedExercise => ({
          name: it.name,
          block,
          format,
          sets_reps: it.sets > 1 ? `${it.sets}x${it.reps}` : it.reps,
          intensity: format === 'prep' ? 'Prehab' : 'Down-regulate',
          target_load: 0,
          rest: '',
          details: it.notes || '',
          rpe: '',
          // DB lookup; may be undefined for synthetic prehab names —
          // GeneratedExercise.trackingType is optional and consumers
          // fall back to name-based classification on undefined.
          trackingType: getTrackingType(it.name),
        });

        const prehab = getInjuryPrehab(locations).map(it => toEx(it, 'Injury Prep', 'prep'));
        const posthab = getInjuryPosthab(locations).map(it => toEx(it, 'Injury Recovery', 'recovery'));

        if (prehab.length > 0) {
          // Slot prehab in right after the last warm-up/prep exercise so it
          // rides inside the warm-up; if there's no prep block, lead with it.
          let lastPrep = -1;
          for (let i = 0; i < enhancedWorkout.length; i++) {
            if (/prep|warm|movement|mobility|activation/i.test(enhancedWorkout[i].block || '')) lastPrep = i;
          }
          enhancedWorkout.splice(lastPrep + 1, 0, ...prehab);
        }
        if (posthab.length > 0) {
          enhancedWorkout.push(...posthab);
        }

        const uniq = Array.from(new Set(locations));
        reasoning.push(`Injury prehab/post-hab injected for: ${uniq.join(', ')}`);
      }
    }
  } catch (e) {
    console.warn('Injury prehab/post-hab injection skipped:', e);
  }

  // ============================================================================
  // STEP 7c: NORMALIZE adjustments[] against final target_loads.
  //
  // Any code path that mutates ex.target_load AFTER the per-exercise
  // loop must keep the corresponding adjustment record in sync, or the
  // pre-workout banner displays a number the workout card disagrees
  // with (e.g., banner says "35 → 20" but card shows 27). Doing one
  // final reconciliation here catches any future mutation paths.
  // ============================================================================
  for (const ex of enhancedWorkout) {
    const finalLoad = ex.target_load;
    if (!finalLoad || finalLoad <= 0) continue;
    const adj = adjustments.find(a => a.exerciseName === ex.name);
    if (adj && adj.adjustedLoad !== finalLoad) {
      adj.adjustedLoad = finalLoad;
      adj.adjustmentPercent = adj.originalLoad > 0
        ? ((finalLoad - adj.originalLoad) / adj.originalLoad) * 100
        : 0;
    }
  }

  // ============================================================================
  // STEP 8: GENERATE FINAL RESULT
  // ============================================================================

  const statsAdjustments = adjustments.filter(a => a.source === 'statistics').length;
  const deloadAdjustments = adjustments.filter(a => a.source === 'deload').length;

  reasoning.push(`Applied ${adjustments.length} adjustments: ${statsAdjustments} statistical, ${deloadAdjustments} deload`);
  
  let message = '';
  if (status === 'deload') {
    message = `DELOAD workout generated. Training load ratio (${acwr.toFixed(1)}) requires reduced volume and intensity to prevent overtraining.`;
  } else if (substitutions.length > 0) {
    message = `Workout optimized. ${substitutions.length} exercise substitutions available for injury prevention.`;
  } else if (adjustments.length > 0) {
    const avgChange = adjustments.reduce((sum, a) => sum + a.adjustmentPercent, 0) / adjustments.length;
    const direction = avgChange > 0 ? 'increased' : 'decreased';
    message = `Workout optimized. ${adjustments.length} exercises ${direction} based on your performance patterns and recovery.`;
  } else {
    message = 'Workout generated. Training on schedule.';
  }
  
  console.log(` Smart Workout: Generated ${enhancedWorkout.length} exercises with ${adjustments.length} adjustments, ${substitutions.length} substitutions`);
  
  return {
    status,
    workout: enhancedWorkout,
    reasoning,
    intelligence: {
      recoveryScore,
      acwr,
      acwrStatus,
      trainingLevel,
      mlReady,
      deloadTriggered,
      volumeAdjustment,
      intensityAdjustment,
      periodizationPhase: periodizationPhase ? {
        name: periodizationPhase.name,
        type: periodizationPhase.type,
        focus: periodizationPhase.focus,
      } : undefined,
      // Reentry context for the session pre-briefing. Populated whenever
      // the reentry gate fired (accepted plan OR auto-detected returning).
      reentry: reentryTriggered ? {
        triggered: true,
        daysOff: reentryDays,
        strengthFactor: reentryFactor,
        aerobicFactor: reentryAerobicFactor,
        strengthCapPct: Math.round(reentryFactor * 100),
        aerobicCapPct: Math.round(reentryAerobicFactor * 100),
      } : undefined,
    },
    adjustments,
    substitutions,
    synthesisContext,
    message,
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Quick synchronous estimate of operator score for training level detection.
 * This is a simplified version that only uses session volume data.
 *
 * NOTE: For accurate operator score, use UnifiedIntelligence.calculateOperatorScore()
 * which includes 1RMs, assessments, recovery metrics, and more comprehensive scoring.
 * This simplified version exists because detectTrainingLevel() needs a sync value.
 */
function estimateOperatorScoreFromSessions(sessions: SessionLog[]): number {
  if (sessions.length < 5) return 50;

  const recentSessions = sessions.slice(-20);
  const avgVolume = recentSessions.reduce((sum, s) => {
    if (s.sessionType !== 'pt' || !('exercises' in s)) return sum;
    const ptSession = s as PTSessionLog;
    return sum + ptSession.exercises.reduce((eSum, e) => {
      return eSum + (e.sets?.reduce((sSum, set) => sSum + computeSetVolume(set, e.exerciseName), 0) || 0);
    }, 0);
  }, 0) / recentSessions.length;

  // Normalize to 0-100 (assuming 20k is good, 50k is excellent)
  return Math.min(100, Math.round((avgVolume / 500)));
}

function calculateDaysInSystem(sessions: SessionLog[]): number {
  if (sessions.length < 2) return 0;
  
  const dates = sessions.map(s => new Date(s.date).getTime()).sort((a, b) => a - b);
  return Math.round((dates[dates.length - 1] - dates[0]) / (24 * 60 * 60 * 1000));
}

function calculateAvgRPE(sessions: SessionLog[]): number {
  let totalRPE = 0;
  let count = 0;
  
  sessions.slice(-20).forEach(session => {
    if (session.sessionType !== 'pt' || !('exercises' in session)) return;
    const ptSession = session as PTSessionLog;
    
    ptSession.exercises.forEach(ex => {
      ex.sets?.forEach(set => {
        if (set.rpe && set.rpe > 0) {
          totalRPE += set.rpe;
          count++;
        }
      });
    });
  });
  
  return count > 0 ? totalRPE / count : 7;
}

function calculateProgressionRate(sessions: SessionLog[]): number {
  // Build per-exercise weight history from the last 8 PT sessions
  const ptSessions = sessions
    .filter((s): s is PTSessionLog => s.sessionType === 'pt' && 'exercises' in s)
    .slice(-8);

  if (ptSessions.length < 4) return 0.025; // Fallback: 2.5% default

  // Track weight per exercise across sessions
  const exerciseWeights: Record<string, number[]> = {};
  for (const session of ptSessions) {
    for (const ex of session.exercises) {
      const sets = ex.sets?.filter(s => s.weight > 0) || [];
      if (sets.length === 0) continue;
      const avgWeight = sets.reduce((sum, s) => sum + s.weight, 0) / sets.length;
      if (!exerciseWeights[ex.exerciseName]) exerciseWeights[ex.exerciseName] = [];
      exerciseWeights[ex.exerciseName].push(avgWeight);
    }
  }

  // Compute average per-session weight change across all exercises
  const rates: number[] = [];
  for (const weights of Object.values(exerciseWeights)) {
    if (weights.length < 2) continue;
    for (let i = 1; i < weights.length; i++) {
      if (weights[i - 1] > 0) {
        rates.push((weights[i] - weights[i - 1]) / weights[i - 1]);
      }
    }
  }

  if (rates.length === 0) return 0.025;

  const avgRate = rates.reduce((sum, r) => sum + r, 0) / rates.length;
  // Clamp to 0–10% range
  return Math.max(0, Math.min(0.10, avgRate));
}

function calculateConsecutiveTrainingDays(sessions: SessionLog[]): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  let consecutive = 0;
  let checkDate = new Date(today);
  
  for (let i = 0; i < 7; i++) {
    checkDate.setDate(checkDate.getDate() - 1);
    const dateStr = checkDate.toISOString().split('T')[0];
    
    const hasSession = sessions.some(s => {
      const sessionDate = new Date(s.date).toISOString().split('T')[0];
      return sessionDate === dateStr && (s.sessionType === 'pt' || s.sessionType === 'assessment');
    });
    
    if (hasSession) {
      consecutive++;
    } else {
      break;
    }
  }
  
  return consecutive;
}

// ============================================================================
// QUICK FUNCTIONS FOR DASHBOARD
// ============================================================================

/**
 * Quick check if training is advisable today
 * Use this on dashboard before showing workout
 */
export async function isTrainingAdvisable(): Promise<{
  advisable: boolean;
  reason: string;
  recoveryScore: number;
  acwr: number;
  recommendation: 'train' | 'light' | 'deload' | 'rest';
}> {
  const latestRecovery = await getTodayRecoveryMetrics();
  const recoveryScore = latestRecovery?.readinessScore ?? 70;
  const recoveryDebt = await calculateRecoveryDebt();
  const acwr = recoveryDebt.acwr;
  
  // Get user's training level for personalized thresholds
  const sessions = await getSessionHistory();
  const ptSessions = sessions.filter(s => s.sessionType === 'pt');
  const trainingLevelResult = detectTrainingLevel({
    operatorScore: estimateOperatorScoreFromSessions(ptSessions),
    totalSessions: ptSessions.length,
    timeInSystemDays: calculateDaysInSystem(ptSessions),
  });
  const thresholds = getTrainingLevelThresholds(trainingLevelResult.level);
  
  // Decision logic
  if (recoveryScore < 40) {
    return {
      advisable: false,
      reason: `Recovery critically low (${recoveryScore}/100). Rest required.`,
      recoveryScore,
      acwr,
      recommendation: 'rest',
    };
  }
  
  if (acwr > thresholds.acwr.max + 0.4) {
    return {
      advisable: false,
      reason: `Training load dangerously high (${acwr.toFixed(1)}). Rest required.`,
      recoveryScore,
      acwr,
      recommendation: 'rest',
    };
  }
  
  if (recoveryDebt.needsDeload || acwr > thresholds.acwr.max + 0.2) {
    return {
      advisable: true,
      reason: `Deload recommended. Training load elevated (${acwr.toFixed(1)}).`,
      recoveryScore,
      acwr,
      recommendation: 'deload',
    };
  }
  
  if (recoveryScore < 60 || acwr > thresholds.acwr.max) {
    return {
      advisable: true,
      reason: `Light session recommended. Recovery or load slightly off.`,
      recoveryScore,
      acwr,
      recommendation: 'light',
    };
  }
  
  return {
    advisable: true,
    reason: 'Good to train. Recovery and load optimal.',
    recoveryScore,
    acwr,
    recommendation: 'train',
  };
}

/**
 * Get a summary of today's training recommendation
 * For display on dashboard
 */
export async function getTodayTrainingSummary(): Promise<{
  recommendation: 'train' | 'light' | 'deload' | 'rest';
  title: string;
  subtitle: string;
  color: 'green' | 'yellow' | 'orange' | 'red';
  metrics: {
    recoveryScore: number;
    acwr: number;
    consecutiveDays: number;
  };
}> {
  const check = await isTrainingAdvisable();
  const sessions = await getSessionHistory();
  const consecutiveDays = calculateConsecutiveTrainingDays(sessions);
  
  const recommendations = {
    train: {
      title: 'Ready to Train',
      subtitle: 'All systems optimal. Hit your targets.',
      color: 'green' as const,
    },
    light: {
      title: 'Light Session',
      subtitle: 'Reduce intensity 10-15%. Focus on technique.',
      color: 'yellow' as const,
    },
    deload: {
      title: 'Deload Day',
      subtitle: '50% volume, 70% intensity. Recovery priority.',
      color: 'orange' as const,
    },
    rest: {
      title: 'Rest Day',
      subtitle: 'Training blocked. Focus on recovery.',
      color: 'red' as const,
    },
  };
  
  const rec = recommendations[check.recommendation];
  
  return {
    recommendation: check.recommendation,
    title: rec.title,
    subtitle: rec.subtitle,
    color: rec.color,
    metrics: {
      recoveryScore: check.recoveryScore,
      acwr: check.acwr,
      consecutiveDays,
    },
  };
}

